// Condition SEO pages (6), restructured into topic sections + grouped FAQ.
// Each page targets "<condition> Νευρολόγος Αθήνα"; local terms woven into prose.
// `sections` render as anchored topics; `whenToSee` renders as a highlighted callout.
export const conditions = [
  {
    slug: { el: "epilipsia", en: "epilepsy" },
    icon: "Zap",
    name: { el: "Επιληψία & Πρώτη Επιληπτική Κρίση", en: "Epilepsy & First Seizure" },
    metaTitle: {
      el: "Επιληψία — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Epilepsy — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Διάγνωση και θεραπεία επιληψίας και πρώτης επιληπτικής κρίσης από εξειδικευμένη νευρολόγο στην Αθήνα (Αμπελόκηποι, Πλ. Μαβίλη). Ηλεκτροεγκεφαλογράφημα και εξατομικευμένη αγωγή.",
      en: "Diagnosis and treatment of epilepsy and first seizures by a specialist neurologist in Athens. EEG and personalised management.",
    },
    excerpt: {
      el: "Διερεύνηση πρώτης κρίσης, διάγνωση επιληψίας με ΗΕΓ και εξατομικευμένη ρύθμιση αγωγής, με εξειδίκευση από κέντρα αριστείας.",
      en: "First-seizure work-up, epilepsy diagnosis with EEG and personalised treatment, with subspecialist training from centres of excellence.",
    },
    intro: {
      el: "Ολοκληρωμένη διάγνωση και θεραπεία της επιληψίας από εξειδικευμένη επιληπτολόγο, με εκπαίδευση στην Christian Doppler Klinik (Salzburg, Austria), επίσημο Ευρωπαϊκό Κέντρο Αναφοράς για σπάνιες και σύνθετες μορφές επιληψίας (Epicare).",
      en: "Comprehensive diagnosis and treatment of epilepsy by a subspecialist epileptologist, trained at Christian Doppler Klinik (Salzburg, Austria), an official European Reference Centre for rare and complex forms of epilepsy (Epicare).",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Η επιληψία είναι μία από τις συχνότερες νευρολογικές παθήσεις και ορίζεται από την τάση για επαναλαμβανόμενες, μη προκλητές επιληπτικές κρίσεις. Οφείλεται σε παροδική, υπερβολική ηλεκτρική δραστηριότητα του εγκεφάλου.",
            "Μια πρώτη, μεμονωμένη κρίση δεν σημαίνει πάντα επιληψία. Στόχος της αρχικής αξιολόγησης είναι να διακριθεί η προκλητή από τη μη προκλητή κρίση, να αναγνωριστεί ο τύπος της και να εκτιμηθεί ο κίνδυνος νέας κρίσης.",
          ],
          en: [
            "Epilepsy is one of the most common neurological conditions and is defined by a tendency to recurrent, unprovoked seizures. It is caused by transient, excessive electrical activity in the brain.",
            "A first, isolated seizure does not always mean epilepsy. The aim of the initial assessment is to distinguish provoked from unprovoked seizures, identify the seizure type and estimate the risk of a new seizure.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα & τύποι κρίσεων", en: "Symptoms & seizure types" },
        body: {
          el: [
            "Οι κρίσεις εκδηλώνονται με διαφορετικό τρόπο ανάλογα με την περιοχή του εγκεφάλου που εμπλέκεται. Μετά την κρίση συχνά ακολουθεί παροδική σύγχυση, κόπωση, μυϊκός πόνος, πονοκέφαλος ή υπνηλία.",
          ],
          en: [
            "Seizures present differently depending on the area of the brain involved. A seizure is often followed by transient confusion, fatigue, muscle ache or headache.",
          ],
        },
        list: {
          el: [
            "Γενικευμένες τονικοκλονικές κρίσεις — απώλεια συνείδησης με σπασμούς",
            "Εστιακές κρίσεις — τοπικές κινητικές, αισθητικές ή ψυχικές εκδηλώσεις",
            "Κρίσεις αφαιρέσεων — σύντομα επεισόδια «κενού» βλέμματος",
            "Προειδοποιητικές αισθήσεις (aura) πριν από ορισμένες κρίσεις",
          ],
          en: [
            "Generalised tonic-clonic seizures — loss of consciousness with convulsions",
            "Focal seizures — localised motor, sensory or psychic features",
            "Absence seizures — brief episodes of a blank stare",
            "Warning sensations (aura) before some seizures",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Αίτια & εκλυτικοί παράγοντες", en: "Causes & triggers" },
        body: {
          el: [
            "Η επιληψία μπορεί να οφείλεται σε δομική βλάβη (π.χ. μετά από εγκεφαλικό, κρανιοεγκεφαλική κάκωση ή λοίμωξη), σε γενετικούς ή μεταβολικούς παράγοντες, ενώ σε αρκετές περιπτώσεις το αίτιο παραμένει άγνωστο. Ορισμένοι παράγοντες μπορεί να προκαλέσουν κρίση σε ευάλωτα άτομα:",
          ],
          en: [
            "Epilepsy may be due to a structural cause (e.g. after stroke, head injury or infection), to genetic or metabolic factors, while in many cases the cause remains unknown. Certain factors can trigger a seizure in susceptible people:",
          ],
        },
        list: {
          el: ["Έντονη στέρηση ύπνου", "Πυρετός ή λοίμωξη", "Υπερβολική κατανάλωση ή απότομη στέρηση αλκοόλ", "Παράλειψη της αγωγής", "Έντονο στρες"],
          en: ["Severe sleep deprivation", "Fever or infection", "Excess alcohol or abrupt alcohol withdrawal", "Missing medication doses", "Intense stress"],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση & Ηλεκτροεγκεφαλογράφημα (ΗΕΓ)", en: "Diagnosis & EEG" },
        body: {
          el: [
            "Η διερεύνηση περιλαμβάνει λεπτομερές ιστορικό, νευρολογική εξέταση, ηλεκτροεγκεφαλογράφημα (ΗΕΓ) και, όπου χρειάζεται, μαγνητική τομογραφία εγκεφάλου.",
            "Το ΗΕΓ καταγράφει την ηλεκτρική δραστηριότητα του εγκεφάλου και μπορεί να αναδείξει επιληπτόμορφες εκφορτίσεις που κατευθύνουν τη διάγνωση. Ως υπεύθυνη Εργαστηρίου Ηλεκτροεγκεφαλογραφίας, η Δρ. Αποστολακοπούλου ερμηνεύει και σύνθετες καταγραφές, συμπεριλαμβανομένων των μακράς διάρκειας Video-ΗΕΓ.",
          ],
          en: [
            "Work-up includes a detailed history, neurological examination, an EEG and, where needed, a brain MRI.",
            "The EEG records the brain's electrical activity and can reveal epileptiform discharges that guide diagnosis. As head of an EEG Laboratory, Dr. Apostolakopoulou also interprets complex recordings, including long-term Video-EEG.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Θεραπευτική προσέγγιση", en: "Treatment approach" },
        body: {
          el: [
            "Η θεραπεία εξατομικεύεται με βάση τον τύπο των κρίσεων, την ηλικία, το ιστορικό και τον τρόπο ζωής. Περιλαμβάνει επιλογή και ρύθμιση αντιεπιληπτικής αγωγής και συμβουλευτική για την ασφάλεια στην καθημερινότητα (οδήγηση, εργασία, ύπνος).",
            "Σε επιλεγμένες περιπτώσεις φαρμακοανθεκτικής επιληψίας, η ρύθμιση νευροδιεγέρτη πνευμονογαστρικού (VNS) — μιας καθιερωμένης, εγκεκριμένης από τον FDA θεραπείας — γίνεται σε συνεργασία με νευροχειρουργό.",
          ],
          en: [
            "Treatment is individualised according to seizure type, age, history and lifestyle. It includes the selection and titration of anti-seizure medication and counselling on everyday safety (driving, work, sleep).",
            "In selected cases of drug-resistant epilepsy, adjustment of a Vagus Nerve Stimulator (VNS) is performed in collaboration with a neurosurgeon.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Επείγον: κρίση που διαρκεί πάνω από 3 λεπτά ή διαδοχικές κρίσεις — καλέστε το 166",
        "Μετά από μια πρώτη επιληπτική κρίση",
        "Όταν οι κρίσεις αλλάζουν χαρακτήρα, συχνότητα ή ένταση",
        "Όταν η υπάρχουσα αγωγή δεν ελέγχει επαρκώς τις κρίσεις",
      ],
      en: [
        "Emergency: a seizure lasting over 3 minutes or seizures in succession — call emergency services",
        "After a first seizure",
        "When seizures change in character, frequency or intensity",
        "When current medication does not adequately control seizures",
      ],
    },
    faq: [
      {
        q: { el: "Μετά από μία κρίση θα παίρνω φάρμακα για πάντα;", en: "Will one seizure mean lifelong medication?" },
        a: {
          el: "Όχι απαραίτητα. Η απόφαση για αγωγή εξαρτάται από τον κίνδυνο νέας κρίσης, ο οποίος εκτιμάται με το ΗΕΓ, την απεικόνιση και το ιστορικό. Σε ορισμένες περιπτώσεις παρακολουθούμε χωρίς φαρμακευτική αγωγή· σε άλλες συνιστάται θεραπεία για συγκεκριμένο διάστημα.",
          en: "Not necessarily. The decision to treat depends on the recurrence risk, estimated from EEG, imaging and history. In some cases we monitor without medication; in others, treatment for a defined period is recommended.",
        },
      },
      {
        q: { el: "Μπορώ να οδηγώ αν έχω επιληψία;", en: "Can I drive if I have epilepsy?" },
        a: {
          el: "Η οδήγηση επιτρέπεται υπό προϋποθέσεις, συνήθως μετά από ελεύθερο κρίσεων διάστημα που ορίζεται από τη νομοθεσία. Θα σας ενημερώσουμε αναλυτικά με βάση την περίπτωσή σας.",
          en: "Driving is allowed under conditions, usually after a seizure-free period defined by law. We will advise you in detail based on your case.",
        },
      },
      {
        q: { el: "Πώς μπορώ να μειώσω τον κίνδυνο νέας κρίσης;", en: "How can I reduce the risk of another seizure?" },
        a: {
          el: "Η σταθερή λήψη της αγωγής, ο επαρκής ύπνος, η αποφυγή υπερβολικού αλκοόλ και η διαχείριση του στρες μειώνουν σημαντικά τον κίνδυνο. Οι εξατομικευμένες οδηγίες δίνονται στην επίσκεψη.",
          en: "Taking medication consistently, getting enough sleep, avoiding excess alcohol and managing stress significantly reduce the risk. Personalised advice is given at the visit.",
        },
      },
      {
        q: { el: "Είναι κληρονομική η επιληψία;", en: "Is epilepsy hereditary?" },
        a: {
          el: "Στις περισσότερες περιπτώσεις όχι. Ορισμένες μορφές έχουν γενετική βάση, αλλά η πλειονότητα δεν κληρονομείται άμεσα.",
          en: "In most cases no. Some forms have a genetic basis, but the majority are not directly inherited.",
        },
      },
      {
        q: { el: "Μπορώ να ασκούμαι ή να κάνω σπορ;", en: "Can I exercise or play sport?" },
        a: {
          el: "Οι περισσότερες δραστηριότητες είναι ασφαλείς με καλό έλεγχο των κρίσεων. Ορισμένα σπορ χρειάζονται προφυλάξεις, όπως αθλήματα σε ύψος, θαλάσσια σπορ ανοιχτής θάλασσας ή μηχανοκίνητος αθλητισμός.",
          en: "Most activities are safe with good seizure control. Certain sports (e.g. swimming or heights) need precautions.",
        },
      },
      {
        q: { el: "Επηρεάζει η αγωγή την εγκυμοσύνη;", en: "Does medication affect pregnancy?" },
        a: {
          el: "Ορισμένα αντιεπιληπτικά χρειάζονται προσαρμογή πριν από μια εγκυμοσύνη. Ο έγκαιρος προγραμματισμός με τον νευρολόγο είναι σημαντικός.",
          en: "Some anti-seizure medications need adjustment before pregnancy. Early planning with your neurologist is important.",
        },
      },
    ],
  },
  {
    slug: { el: "kefalalgies-imikrania", en: "headaches-migraine" },
    icon: "Activity",
    name: { el: "Κεφαλαλγίες & Ημικρανία", en: "Headaches & Migraine" },
    metaTitle: {
      el: "Κεφαλαλγίες & Ημικρανία — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Headaches & Migraine — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Διάγνωση και θεραπεία ημικρανίας, χρόνιας κεφαλαλγίας και νευραλγιών από νευρολόγο στην Αθήνα. Botox για χρόνια ημικρανία και αποκλεισμός ινιακού νεύρου.",
      en: "Diagnosis and treatment of migraine, chronic headache and neuralgias by a neurologist in Athens. Botox for chronic migraine and occipital nerve block.",
    },
    excerpt: {
      el: "Εξατομικευμένη αντιμετώπιση ημικρανίας και χρόνιων κεφαλαλγιών, με σύγχρονες φαρμακευτικές και επεμβατικές επιλογές.",
      en: "Personalised management of migraine and chronic headaches, with modern pharmacological and interventional options.",
    },
    intro: {
      el: "Στοχευμένη διάγνωση και σύγχρονη αντιμετώπιση της ημικρανίας και των χρόνιων κεφαλαλγιών, με πιστοποιημένη εκπαίδευση στη διαχείριση κεφαλαλγιών.",
      en: "Focused diagnosis and modern management of migraine and chronic headaches, with certified headache-management training.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Οι κεφαλαλγίες είναι από τους συχνότερους λόγους επίσκεψης στον νευρολόγο και καλύπτουν ένα ευρύ φάσμα — από την ημικρανία και την κεφαλαλγία τάσης έως τη νευραλγία τριδύμου και την ινιακή νευραλγία.",
            "Στόχος δεν είναι μόνο η ανακούφιση από την κρίση, αλλά και η μείωση της συχνότητας και της έντασης, με βάση τα σύγχρονα επιστημονικά δεδομένα και χωρίς υπερβολικές υποσχέσεις.",
          ],
          en: [
            "Headaches are among the most common reasons for a neurology visit and span a wide range — from migraine and tension-type headache to trigeminal neuralgia and occipital neuralgia.",
            "The aim is not only to relieve the attack, but also to reduce its frequency and intensity, grounded in current scientific evidence and without overpromising.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα & τύποι", en: "Symptoms & types" },
        body: {
          el: ["Η αναγνώριση του τύπου της κεφαλαλγίας είναι το πρώτο βήμα για τη σωστή αντιμετώπιση:"],
          en: ["Recognising the headache type is the first step towards the right treatment:"],
        },
        list: {
          el: [
            "Ημικρανία — μέτριος ή έντονος, σφύζων πόνος, συχνά στη μία πλευρά, με ναυτία ή ευαισθησία στο φως και τον ήχο",
            "Κεφαλαλγία τάσης — πιεστικός, αμφοτερόπλευρος, ηπιότερος πόνος",
            "Αθροιστική κεφαλαλγία — πολύ έντονος πόνος γύρω από το μάτι",
            "Νευραλγίες (τριδύμου, ινιακή) — οξύς πόνος «σαν ηλεκτρισμός / ηλεκτρικό ρεύμα»",
          ],
          en: [
            "Migraine — moderate-to-severe, throbbing pain, often one-sided, with nausea or sensitivity to light and sound",
            "Tension-type headache — pressing, bilateral, milder pain",
            "Cluster headache — very severe pain around the eye",
            "Neuralgias (trigeminal, occipital) — sharp, electric-shock-like pain",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Εκλυτικοί παράγοντες", en: "Triggers" },
        body: {
          el: ["Η ημικρανία συχνά πυροδοτείται ή επιδεινώνεται από συγκεκριμένους παράγοντες, η αναγνώριση των οποίων βοηθά στην πρόληψη:"],
          en: ["Migraine is often triggered or worsened by specific factors; recognising them helps with prevention:"],
        },
        list: {
          el: ["Στρες και αλλαγές στη ρουτίνα", "Διαταραχές ή έλλειψη ύπνου", "Ορμονικές μεταβολές", "Αφυδάτωση ή παράλειψη γευμάτων", "Παρατεταμένη έκθεση σε οθόνες ή έντονο φως"],
          en: ["Stress and changes in routine", "Sleep disturbance or lack of sleep", "Hormonal fluctuations", "Dehydration or skipping meals", "Prolonged screen exposure or bright light"],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση", en: "Diagnosis" },
        body: {
          el: [
            "Η σωστή διάγνωση ξεκινά από λεπτομερές ιστορικό: τα χαρακτηριστικά του πόνου, η συχνότητα, οι εκλυτικοί παράγοντες και η επίδραση στην καθημερινότητα.",
            "Στόχος είναι να διακριθούν οι πρωτοπαθείς κεφαλαλγίες (όπως η ημικρανία) από δευτεροπαθή αίτια που χρειάζονται περαιτέρω διερεύνηση, και να αναγνωριστούν προειδοποιητικά σημεία που απαιτούν άμεση εκτίμηση ή απεικόνιση.",
          ],
          en: [
            "Accurate diagnosis begins with a detailed history: the character of the pain, its frequency, triggers and impact on daily life.",
            "The aim is to distinguish primary headaches (such as migraine) from secondary causes that require further investigation, and to recognise red-flag features that need urgent assessment or imaging.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Θεραπευτική προσέγγιση", en: "Treatment approach" },
        body: {
          el: [
            "Η ημικρανία αντιμετωπίζεται σε δύο άξονες: την αντιμετώπιση της κρίσης και την προληπτική αγωγή που μειώνει τη συχνότητα και την ένταση. Το πλάνο εξατομικεύεται και μπορεί να περιλαμβάνει φαρμακευτική αγωγή και τροποποίηση παραγόντων.",
            "Για τη χρόνια ημικρανία (πονοκέφαλος 15 ή περισσότερες ημέρες τον μήνα) διατίθεται η εγκεκριμένη θεραπεία με βοτουλινική τοξίνη (Botox), συνήθως κάθε 12 εβδομάδες. Σε επιλεγμένες κεφαλαλγίες και νευραλγίες εφαρμόζεται αποκλεισμός ινιακού νεύρου.",
          ],
          en: [
            "Migraine is treated on two fronts: acute management of the attack and preventive therapy that reduces frequency and intensity. The plan is individualised and may include medication and modification of contributing factors.",
            "For chronic migraine (headache on 15 or more days per month), the approved botulinum toxin (Botox) treatment is available, usually every 12 weeks. For selected headaches and neuralgias, an occipital nerve block is used.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Επείγον: ξαφνικός, πολύ έντονος πονοκέφαλος «σαν κεραυνός» — άμεση εκτίμηση",
        "Νέα κεφαλαλγία μετά τα 50 ή αλλαγή στο συνηθισμένο μοτίβο",
        "Κεφαλαλγία με πυρετό, νευρολογικά συμπτώματα ή μετά από τραυματισμό",
        "Πονοκέφαλοι που επιδεινώνονται ή δεν ανταποκρίνονται στα συνήθη μέτρα",
      ],
      en: [
        "Emergency: a sudden, very severe 'thunderclap' headache — seek immediate assessment",
        "A new headache after 50 or a change in your usual pattern",
        "Headache with fever, neurological symptoms or after an injury",
        "Headaches that are worsening or not responding to usual measures",
      ],
    },
    faq: [
      {
        q: { el: "Πώς ξεχωρίζω την ημικρανία από έναν απλό πονοκέφαλο;", en: "How do I tell migraine from an ordinary headache?" },
        a: {
          el: "Η ημικρανία είναι συνήθως μέτριος ή έντονος, σφύζων πόνος, συχνά στη μία πλευρά, που επιδεινώνεται με την κίνηση και συνοδεύεται από ναυτία ή ευαισθησία στο φως και τον ήχο. Η κεφαλαλγία τάσης είναι πιεστική και ηπιότερη. Η ακριβής διάκριση γίνεται κλινικά.",
          en: "Migraine is usually a moderate-to-severe, throbbing pain, often one-sided, worsened by movement and accompanied by nausea or sensitivity to light and sound. Tension-type headache is pressing and milder. The precise distinction is made clinically.",
        },
      },
      {
        q: { el: "Κάθε πότε επαναλαμβάνεται η θεραπεία με Botox;", en: "How often is Botox treatment repeated?" },
        a: {
          el: "Στη χρόνια ημικρανία η θεραπεία επαναλαμβάνεται συνήθως κάθε 12 εβδομάδες, με επανεκτίμηση της ανταπόκρισης σε κάθε συνεδρία.",
          en: "For chronic migraine, treatment is usually repeated every 12 weeks, with reassessment of the response at each session.",
        },
      },
      {
        q: { el: "Μπορεί να προληφθεί η ημικρανία;", en: "Can migraine be prevented?" },
        a: {
          el: "Ναι. Με προληπτική αγωγή, αναγνώριση και αποφυγή εκλυτικών παραγόντων και σταθερή ρουτίνα ύπνου και γευμάτων, πολλοί ασθενείς πετυχαίνουν σημαντική μείωση της συχνότητας των κρίσεων.",
          en: "Yes. With preventive treatment, identifying and avoiding triggers and a steady sleep and meal routine, many patients achieve a significant reduction in attack frequency.",
        },
      },
      {
        q: { el: "Τι είναι η αύρα (προειδοποιητικά σημεία);", en: "What is an aura?" },
        a: {
          el: "Η αύρα είναι μια προειδοποιητική αίσθηση πριν από την ημικρανία — συχνά οπτική (λάμψεις, θολή όραση) ή αισθητική — που διαρκεί λίγα λεπτά. Παρατηρείται σε ένα ποσοστό 20% με 30% του συνόλου των ατόμων που πάσχουν από ημικρανίες.",
          en: "An aura is a warning sensation before a migraine — often visual (flashes, blurring) or sensory — lasting a few minutes. It is observed in roughly 20% to 30% of people who experience migraines.",
        },
      },
      {
        q: { el: "Οι νεότερες θεραπείες (anti-CGRP) είναι επιλογή;", en: "Are newer (anti-CGRP) treatments an option?" },
        a: {
          el: "Για επιλεγμένες περιπτώσεις ημικρανίας υπάρχουν σύγχρονες προληπτικές θεραπείες. Η καταλληλότητα εκτιμάται εξατομικευμένα.",
          en: "For selected migraine cases there are modern preventive therapies. Eligibility is assessed individually.",
        },
      },
      {
        q: { el: "Μπορεί το συχνό παυσίπονο να χειροτερεύσει τον πονοκέφαλο;", en: "Can frequent painkillers worsen a headache?" },
        a: {
          el: "Ναι. Η υπερβολική χρήση παυσίπονων μπορεί να οδηγήσει σε κεφαλαλγία από κατάχρηση φαρμάκων· γι' αυτό η προληπτική αγωγή είναι σημαντική.",
          en: "Yes. Overusing painkillers can lead to medication-overuse headache, which is why preventive treatment matters.",
        },
      },
    ],
  },
  {
    slug: { el: "diataraches-mnimis-anoia", en: "memory-disorders-dementia" },
    icon: "Brain",
    name: { el: "Διαταραχές Μνήμης & Άνοια / Alzheimer", en: "Memory Disorders & Dementia / Alzheimer's" },
    metaTitle: {
      el: "Διαταραχές Μνήμης & Άνοια — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Memory Disorders & Dementia — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Έλεγχος μνήμης και διάγνωση ήπιας νοητικής διαταραχής και άνοιας τύπου Alzheimer από νευρολόγο στην Αθήνα. Ετήσιος έλεγχος με νευροψυχολογικό screening.",
      en: "Memory assessment and diagnosis of mild cognitive impairment and Alzheimer's dementia by a neurologist in Athens. Annual check-up with cognitive screening.",
    },
    excerpt: {
      el: "Δομημένος έλεγχος μνήμης για την έγκαιρη διάκριση της φυσιολογικής γήρανσης από την ήπια νοητική διαταραχή και την άνοια.",
      en: "A structured memory assessment for the early distinction of normal ageing from mild cognitive impairment and dementia.",
    },
    intro: {
      el: "Δομημένη αξιολόγηση της μνήμης για την έγκαιρη αναγνώριση αλλαγών, με ερευνητικό υπόβαθρο στη νοητική έκπτωση.",
      en: "A structured memory assessment for the early recognition of changes, backed by research on cognitive decline.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Οι διαταραχές μνήμης και γνωστικών λειτουργιών επηρεάζουν τη μνήμη, τη συγκέντρωση και άλλες νοητικές ικανότητες — από ήπιες μορφές (ήπια νοητική διαταραχή, MCI) έως πιο εξελιγμένες, όπως η άνοια τύπου Alzheimer.",
            "Η έγκαιρη αξιολόγηση είναι σημαντική, γιατί ορισμένα αίτια νοητικής έκπτωσης είναι αναστρέψιμα και επειδή η έγκαιρη υποστήριξη βελτιώνει την πορεία και την ποιότητα ζωής.",
          ],
          en: [
            "Memory and cognitive disorders affect memory, concentration and other mental abilities — from mild forms (mild cognitive impairment, MCI) to more advanced ones such as Alzheimer's dementia.",
            "Early assessment matters, because some causes of cognitive decline are reversible and because early support improves the course and quality of life.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα & προειδοποιητικά σημεία", en: "Symptoms & warning signs" },
        body: {
          el: ["Η περιστασιακή αφηρημάδα είναι συχνά φυσιολογική, ιδίως σε περιόδους κόπωσης ή άγχους. Ανησυχητικά είναι τα σημεία που επηρεάζουν την καθημερινότητα:"],
          en: ["Occasional forgetfulness is often normal, especially when tired or stressed. Warning signs are those that affect daily life:"],
        },
        list: {
          el: [
            "Επανάληψη των ίδιων ερωτήσεων ή ιστοριών",
            "Δυσκολία σε γνωστές διαδρομές ή προσανατολισμό",
            "Δυσχέρεια στη διαχείριση οικονομικών ή φαρμάκων",
            "Δυσκολία εύρεσης λέξεων ή παρακολούθησης συζήτησης",
          ],
          en: [
            "Repeating the same questions or stories",
            "Difficulty on familiar routes or with orientation",
            "Trouble managing finances or medication",
            "Difficulty finding words or following a conversation",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Αίτια", en: "Causes" },
        body: {
          el: [
            "Η νοητική έκπτωση μπορεί να οφείλεται σε νευροεκφυλιστικές παθήσεις (όπως η νόσος Alzheimer), σε αγγειακά αίτια, αλλά και σε αναστρέψιμες καταστάσεις — όπως διαταραχές του θυρεοειδούς, έλλειψη βιταμίνης B12, κατάθλιψη ή ανεπιθύμητες ενέργειες φαρμάκων.",
            "Γι' αυτό η αξιολόγηση στοχεύει πάντα και στον αποκλεισμό αναστρέψιμων αιτίων.",
          ],
          en: [
            "Cognitive decline may be due to neurodegenerative conditions (such as Alzheimer's disease), vascular causes, but also reversible conditions — such as thyroid disorders, vitamin B12 deficiency, depression or medication side effects.",
            "For this reason, assessment always also aims to exclude reversible causes.",
          ],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση & έλεγχος μνήμης", en: "Diagnosis & memory testing" },
        body: {
          el: [
            "Η αξιολόγηση περιλαμβάνει λήψη ιστορικού, νευρολογική εξέταση και δομημένο έλεγχο μνήμης (νευροψυχολογικό screening). Όπου χρειάζεται, γίνεται παραπομπή για απεικόνιση εγκεφάλου, εργαστηριακό έλεγχο ή πληρέστερη νευροψυχολογική αξιολόγηση.",
            "Ο ετήσιος έλεγχος μνήμης προσφέρεται ως δομημένη επίσκεψη που συνδυάζει τη νευρολογική εκτίμηση με το τεστ μνήμης, ώστε να παρακολουθείται η πορεία στον χρόνο.",
          ],
          en: [
            "Assessment includes history taking, a neurological examination and a structured memory (neuropsychological) screening. Where needed, referral is made for brain imaging, laboratory tests or a fuller neuropsychological evaluation.",
            "The annual memory check-up is offered as a structured visit combining neurological assessment with a memory test, so that the course can be monitored over time.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Διαχείριση & υποστήριξη", en: "Management & support" },
        body: {
          el: [
            "Η προσέγγιση είναι ολιστική: περιλαμβάνει, όπου ενδείκνυται, φαρμακευτική αγωγή, συμβουλευτική για τροποποιήσιμους παράγοντες υγείας του εγκεφάλου και υποστήριξη του ασθενούς και της οικογένειας.",
            "Οι επισκέψεις πραγματοποιούνται σε ήρεμο περιβάλλον που διευκολύνει τόσο τον ασθενή όσο και τους οικείους του. Η σαφής ενημέρωση αποτελεί βασικό μέρος της φροντίδας.",
          ],
          en: [
            "The approach is holistic: it includes, where indicated, medication, counselling on modifiable brain-health factors and support for the patient and family.",
            "Visits take place in a calm environment that supports both the patient and their relatives. Clear information is a core part of the care.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Όταν η αφηρημάδα αρχίζει να επηρεάζει την καθημερινότητα ή την εργασία",
        "Όταν η οικογένεια παρατηρεί αλλαγές στη μνήμη ή τη συμπεριφορά",
        "Σε ξαφνική ή γρήγορη επιδείνωση της μνήμης",
        "Ως προληπτικός ετήσιος έλεγχος, συχνά μετά τα 60 ή με οικογενειακό ιστορικό",
      ],
      en: [
        "When forgetfulness starts to affect daily life or work",
        "When family notice changes in memory or behaviour",
        "With a sudden or rapid worsening of memory",
        "As a preventive annual check, often after 60 or with a family history",
      ],
    },
    faq: [
      {
        q: { el: "Από ποια ηλικία αξίζει ένας έλεγχος μνήμης;", en: "From what age is a memory check worthwhile?" },
        a: {
          el: "Ένας δομημένος έλεγχος έχει αξία σε κάθε ηλικία εφόσον υπάρχουν συμπτώματα ή ανησυχία. Ως προληπτικός ετήσιος έλεγχος, συνιστάται συχνά μετά τα 60, ή νωρίτερα σε άτομα με οικογενειακό ιστορικό ή παράγοντες κινδύνου.",
          en: "A structured check is valuable at any age when there are symptoms or concern. As a preventive annual check, it is often recommended after 60, or earlier for people with a family history or risk factors.",
        },
      },
      {
        q: { el: "Είναι πάντα μη αναστρέψιμη η απώλεια μνήμης;", en: "Is memory loss always irreversible?" },
        a: {
          el: "Όχι. Ορισμένα αίτια, όπως έλλειψη βιταμινών, διαταραχές θυρεοειδούς, διαταραχές διάθεσης ή λήψη φαρμακευτικής αγωγής, είναι αναστρέψιμα. Γι' αυτό η έγκαιρη και σωστή διερεύνηση είναι σημαντική.",
          en: "No. Some causes, such as vitamin deficiency, thyroid disorders, mood disorders or the use of medication, are reversible. This is why timely and correct investigation is important.",
        },
      },
      {
        q: { el: "Η άνοια είναι ίδια με τη νόσο Αλτσχάιμερ;", en: "Is dementia the same as Alzheimer's disease?" },
        a: {
          el: "Όχι. Η άνοια είναι ένας γενικός όρος-«ομπρέλα» που περιγράφει συμπτώματα απώλειας μνήμης και άλλων νοητικών ικανοτήτων. Η νόσος Αλτσχάιμερ είναι ένας συγκεκριμένος υπότυπος και η συχνότερη αιτία άνοιας, που αφορά περίπου το 60–80% των περιστατικών. Υπάρχουν και άλλες μορφές, όπως η αγγειακή, η μικτή και η μετωποκροταφική άνοια.",
          en: "No. Dementia is an umbrella term describing symptoms of memory loss and other cognitive difficulties. Alzheimer's disease is a specific subtype and the most common cause of dementia, accounting for around 60–80% of cases. Other forms include vascular, mixed and frontotemporal dementia.",
        },
      },
      {
        q: { el: "Πόσο διαρκεί ο ετήσιος έλεγχος μνήμης;", en: "How long does the annual memory check-up take?" },
        a: {
          el: "Ο ετήσιος έλεγχος μνήμης διαρκεί 60 λεπτά, με χρόνο για νευρολογική εκτίμηση, τεστ μνήμης και σαφή ενημέρωση για τα ευρήματα και το πλάνο παρακολούθησης.",
          en: "The annual memory check-up lasts 60 minutes, with time for a neurological assessment, memory test and clear discussion of the findings and follow-up plan.",
        },
      },
      {
        q: { el: "Μπορεί να προληφθεί η άνοια;", en: "Can dementia be prevented?" },
        a: {
          el: "Η διαχείριση παραγόντων κινδύνου, η άσκηση, η κοινωνική δραστηριότητα και η νοητική εγρήγορση μειώνουν τον κίνδυνο εμφάνισης άνοιας.",
          en: "Managing risk factors, exercise, social activity and mental alertness reduce the risk of developing dementia.",
        },
      },
      {
        q: { el: "Πρέπει να έρθει και συγγενής στην επίσκεψη;", en: "Should a relative come to the visit?" },
        a: {
          el: "Βοηθά πολύ. Ένας οικείος μπορεί να δώσει πληροφορίες για αλλαγές που ο ίδιος ο ασθενής μπορεί να μην αντιλαμβάνεται.",
          en: "It helps a lot. A relative can provide information on changes the patient may not notice.",
        },
      },
      {
        q: { el: "Υπάρχει θεραπεία για τη νόσο Alzheimer;", en: "Is there a treatment for Alzheimer's?" },
        a: {
          el: "Υπάρχουν περιορισμένα δεδομένα από νοσοτροποποιητικά φάρμακα (μονοκλωνικά αντισώματα) που χορηγούνται σε αρχικά στάδια της νόσου. Οι συνήθεις αγωγές και τα υποστηρικτικά μέτρα βελτιώνουν τα συμπτώματα και την ποιότητα ζωής, στοχεύουν σε σταθεροποίηση και καθυστερούν την εξέλιξη της νόσου.",
          en: "There are limited data from disease-modifying drugs (monoclonal antibodies) given in the early stages of the disease. Standard treatments and supportive measures improve symptoms and quality of life, aim at stabilisation and slow the disease's progression.",
        },
      },
    ],
  },
  {
    slug: { el: "parkinson-kinitikes-diataraches", en: "parkinsons-movement-disorders" },
    icon: "Wind",
    name: { el: "Νόσος Πάρκινσον & Κινητικές Διαταραχές", en: "Parkinson's Disease & Movement Disorders" },
    metaTitle: {
      el: "Νόσος Πάρκινσον & Κινητικές Διαταραχές — Νευρολόγος Αθήνα",
      en: "Parkinson's & Movement Disorders — Neurologist Athens",
    },
    metaDescription: {
      el: "Διάγνωση και παρακολούθηση νόσου Πάρκινσον, τρόμου και δυστονίας από νευρολόγο στην Αθήνα. Εξατομικευμένη αγωγή και θεραπεία με βοτουλινική τοξίνη.",
      en: "Diagnosis and monitoring of Parkinson's disease, tremor and dystonia by a neurologist in Athens. Personalised treatment and botulinum toxin therapy.",
    },
    excerpt: {
      el: "Αξιολόγηση και διαχείριση νόσου Πάρκινσον, ιδιοπαθούς τρόμου και δυστονίας, με εξατομικευμένο θεραπευτικό πλάνο.",
      en: "Assessment and management of Parkinson's disease, essential tremor and dystonia, with an individualised treatment plan.",
    },
    intro: {
      el: "Διαγνωστική αξιολόγηση και μακροχρόνια παρακολούθηση κινητικών διαταραχών, με κλινική εμπειρία σε μονάδα κινητικών διαταραχών.",
      en: "Diagnostic assessment and long-term monitoring of movement disorders, with clinical experience in a movement-disorders unit.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Οι κινητικές διαταραχές περιλαμβάνουν παθήσεις που επηρεάζουν την κίνηση, όπως η νόσος Πάρκινσον, ο ιδιοπαθής τρόμος, η δυστονία και το σύνδρομο ανήσυχων ποδιών.",
            "Η έγκαιρη και σωστή διάγνωση, σε συνδυασμό με μια εξατομικευμένη, μακροχρόνια παρακολούθηση, είναι καθοριστική για τη διατήρηση της λειτουργικότητας και της ποιότητας ζωής.",
          ],
          en: [
            "Movement disorders include conditions affecting movement, such as Parkinson's disease, essential tremor, dystonia and restless legs syndrome.",
            "Timely and accurate diagnosis, combined with individualised, long-term follow-up, is decisive for preserving function and quality of life.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα", en: "Symptoms" },
        body: {
          el: ["Η νόσος Πάρκινσον εκδηλώνεται συχνά με μια σταδιακή εμφάνιση κινητικών και μη κινητικών συμπτωμάτων:"],
          en: ["Parkinson's disease often presents with a gradual onset of motor and non-motor symptoms:"],
        },
        list: {
          el: [
            "Βραδυκινησία — επιβράδυνση των κινήσεων",
            "Τρόμος ηρεμίας, συνήθως στο ένα χέρι",
            "Δυσκαμψία και μεταβολές στη βάδιση",
            "Μη κινητικά συμπτώματα — διαταραχές ύπνου, όσφρησης ή διάθεσης",
          ],
          en: [
            "Bradykinesia — slowness of movement",
            "Resting tremor, usually in one hand",
            "Stiffness and changes in gait",
            "Non-motor symptoms — sleep, smell or mood disturbances",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Τύποι & διαφορική διάγνωση", en: "Types & differential diagnosis" },
        body: {
          el: [
            "Δεν είναι κάθε τρόμος νόσος Πάρκινσον. Ο πιο συχνός τρόμος είναι ο ιδιοπαθής τρόμος, που διαφέρει από τον τρόμο του Πάρκινσον. Άλλες κινητικές διαταραχές, όπως η δυστονία, έχουν διαφορετική εικόνα και αντιμετώπιση.",
            "Η προσεκτική διαφορική διάγνωση από καταστάσεις με παρόμοια εικόνα είναι απαραίτητη για τη σωστή θεραπεία.",
          ],
          en: [
            "Not every tremor is Parkinson's disease. The most common tremor is essential tremor, which differs from Parkinsonian tremor. Other movement disorders, such as dystonia, have a different picture and treatment.",
            "Careful differentiation from conditions with a similar picture is essential for the right treatment.",
          ],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση", en: "Diagnosis" },
        body: {
          el: [
            "Η διάγνωση είναι κατά βάση κλινική. Η αρχική εκτίμηση περιλαμβάνει λεπτομερή εξέταση του τύπου της κίνησης και των συμπτωμάτων, διαφορική διάγνωση και, όπου ενδείκνυται, απεικονιστικό έλεγχο.",
          ],
          en: [
            "Diagnosis is largely clinical. Initial assessment includes a detailed examination of the movement pattern and symptoms, differential diagnosis and, where indicated, imaging.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Θεραπευτική προσέγγιση", en: "Treatment approach" },
        body: {
          el: [
            "Η θεραπεία περιλαμβάνει φαρμακευτική αγωγή προσαρμοσμένη στα συμπτώματα και στο στάδιο, καθώς και, όπου ενδείκνυται, θεραπεία με βοτουλινική τοξίνη για εστιακή δυστονία ή σχετιζόμενες εκδηλώσεις.",
            "Η τακτική παρακολούθηση επιτρέπει την έγκαιρη προσαρμογή της αγωγής καθώς εξελίσσονται οι ανάγκες του ασθενούς, με έμφαση στη διατήρηση της λειτουργικότητας.",
          ],
          en: [
            "Treatment includes medication tailored to symptoms and stage and, where indicated, botulinum toxin therapy for focal dystonia or related manifestations.",
            "Regular follow-up allows treatment to be adjusted in good time as the patient's needs evolve, with emphasis on preserving function.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Νέος τρόμος, ιδίως σε ηρεμία και στη μία πλευρά",
        "Επιβράδυνση των κινήσεων, δυσκαμψία ή αλλαγές στη βάδιση",
        "Επαναλαμβανόμενες πτώσεις ή αστάθεια",
        "Ακούσιες συσπάσεις ή αφύσικες στάσεις (δυστονία)",
      ],
      en: [
        "A new tremor, especially at rest and on one side",
        "Slowness of movement, stiffness or changes in gait",
        "Recurrent falls or unsteadiness",
        "Involuntary contractions or abnormal postures (dystonia)",
      ],
    },
    faq: [
      {
        q: { el: "Ο τρόμος σημαίνει πάντα νόσο Πάρκινσον;", en: "Does tremor always mean Parkinson's disease?" },
        a: {
          el: "Όχι. Ο πιο συχνός τρόμος είναι ο ιδιοπαθής τρόμος, που διαφέρει από τον τρόμο του Πάρκινσον. Η διάκριση γίνεται κλινικά, εξετάζοντας πότε εμφανίζεται ο τρόμος και ποια συμπτώματα τον συνοδεύουν.",
          en: "No. The most common tremor is essential tremor, which differs from Parkinsonian tremor. The distinction is made clinically, by examining when the tremor appears and which symptoms accompany it.",
        },
      },
      {
        q: { el: "Θεραπεύεται η νόσος Πάρκινσον;", en: "Can Parkinson's disease be cured?" },
        a: {
          el: "Δεν υπάρχει επί του παρόντος θεραπεία που να την εξαλείφει, ωστόσο με σύγχρονη αγωγή και τακτική παρακολούθηση τα συμπτώματα ελέγχονται αποτελεσματικά και διατηρείται η ποιότητα ζωής για πολλά χρόνια.",
          en: "There is currently no cure, but with modern treatment and regular follow-up the symptoms are managed effectively and quality of life is preserved for many years.",
        },
      },
      {
        q: { el: "Πόσο συχνά χρειάζεται παρακολούθηση;", en: "How often is follow-up needed?" },
        a: {
          el: "Η συχνότητα εξατομικεύεται ανάλογα με το στάδιο και την ανταπόκριση στην αγωγή. Η τακτική επανεκτίμηση επιτρέπει την έγκαιρη προσαρμογή της θεραπείας.",
          en: "The frequency is individualised according to the stage and response to treatment. Regular reassessment allows treatment to be adjusted in good time.",
        },
      },
      {
        q: { el: "Ποια είναι τα πρώτα σημάδια του Πάρκινσον;", en: "What are the first signs of Parkinson's?" },
        a: {
          el: "Συχνά ένας διακριτικός τρόμος στο ένα χέρι, επιβράδυνση κινήσεων, δυσκαμψία ή αλλαγή στη γραφή και τη βάδιση.",
          en: "Often a subtle tremor in one hand, slowness of movement, stiffness or a change in handwriting and gait.",
        },
      },
      {
        q: { el: "Βοηθά η άσκηση;", en: "Does exercise help?" },
        a: {
          el: "Ναι. Η τακτική άσκηση και η φυσικοθεραπεία βελτιώνουν την κινητικότητα, την ισορροπία και τη γενική λειτουργικότητα.",
          en: "Yes. Regular exercise and physiotherapy improve mobility, balance and overall function.",
        },
      },
      {
        q: { el: "Είναι κληρονομική η νόσος Πάρκινσον;", en: "Is Parkinson's disease hereditary?" },
        a: {
          el: "Στις περισσότερες περιπτώσεις όχι. Ένα μικρό ποσοστό σχετίζεται με γενετικούς παράγοντες.",
          en: "In most cases no. A small proportion is linked to genetic factors.",
        },
      },
    ],
  },
  {
    slug: { el: "zali-iligos", en: "dizziness-vertigo" },
    icon: "Compass",
    name: { el: "Ζάλη & Ίλιγγος", en: "Dizziness & Vertigo" },
    metaTitle: {
      el: "Ζάλη & Ίλιγγος — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Dizziness & Vertigo — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Διερεύνηση ζάλης, ιλίγγου και αστάθειας βάδισης από νευρολόγο στην Αθήνα. Διαφορική διάγνωση νευρολογικών και αιθουσαίων αιτίων.",
      en: "Investigation of dizziness, vertigo and gait instability by a neurologist in Athens. Differential diagnosis of neurological and vestibular causes.",
    },
    excerpt: {
      el: "Στοχευμένη διερεύνηση της ζάλης και του ιλίγγου, με διάκριση των νευρολογικών από τα αιθουσαία και άλλα αίτια.",
      en: "Focused work-up of dizziness and vertigo, distinguishing neurological from vestibular and other causes.",
    },
    intro: {
      el: "Συστηματική προσέγγιση της ζάλης και του ιλίγγου, με στόχο τη σαφή διάγνωση και τον αποκλεισμό σοβαρών αιτίων.",
      en: "A systematic approach to dizziness and vertigo, aiming for a clear diagnosis and the exclusion of serious causes.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Η ζάλη και ο ίλιγγος είναι συχνά συμπτώματα με πολλές πιθανές αιτίες — νευρολογικές, αιθουσαίες ή άλλες. Μπορεί να εκδηλωθούν ως αίσθηση περιστροφής (ίλιγγος), αστάθεια βάδισης ή γενικευμένη ζάλη.",
            "Η προσέγγιση είναι συστηματική, με στόχο τη σαφή διάγνωση και την ασφαλή αντιμετώπιση.",
          ],
          en: [
            "Dizziness and vertigo are common symptoms with many possible causes — neurological, vestibular or other. They may present as a spinning sensation (vertigo), unsteadiness of gait or generalised light-headedness.",
            "The approach is systematic, aiming for a clear diagnosis and safe management.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Τύποι ζάλης", en: "Types of dizziness" },
        body: {
          el: ["Η διάκριση του τύπου της ζάλης είναι το πρώτο βήμα, καθώς κατευθύνει τη διερεύνηση:"],
          en: ["Determining the type of dizziness is the first step, as it guides the work-up:"],
        },
        list: {
          el: [
            "Ίλιγγος — αίσθηση περιστροφής του περιβάλλοντος",
            "Αστάθεια — αίσθημα ότι θα χάσετε την ισορροπία σας",
            "Προλιποθυμική ζάλη — αίσθημα «λιποθυμίας» ή σκοτοδίνης",
            "Θέσης — ίλιγγος που προκαλείται από αλλαγές στη θέση του κεφαλιού (BPPV)",
          ],
          en: [
            "Vertigo — a sensation that the surroundings are spinning",
            "Unsteadiness — a feeling of losing your balance",
            "Pre-syncopal dizziness — a feeling of faintness or light-headedness",
            "Positional — vertigo triggered by changes in head position (BPPV)",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Αίτια", en: "Causes" },
        body: {
          el: [
            "Στα αίτια περιλαμβάνονται αιθουσαία προβλήματα (καλοήθης παροξυσμικός ίλιγγος θέσης, αιθουσαία νευρωνίτιδα), η αιθουσαία ημικρανία, ορισμένες νευρολογικές καταστάσεις, αλλά και καρδιολογικά ή ωτορινολαρυγγολογικά αίτια.",
          ],
          en: [
            "Causes include vestibular problems (benign paroxysmal positional vertigo, vestibular neuritis), vestibular migraine, certain neurological conditions, but also cardiac or ENT causes.",
          ],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση", en: "Diagnosis" },
        body: {
          el: [
            "Το λεπτομερές ιστορικό και η νευρολογική εξέταση είναι καθοριστικά. Η αξιολόγηση περιλαμβάνει κλινική διερεύνηση της αιτίας, διαφοροποίηση από καρδιολογικά ή ΩΡΛ αίτια και, όπου χρειάζεται, παραπομπή για συμπληρωματικό έλεγχο.",
          ],
          en: [
            "A detailed history and neurological examination are decisive. Assessment includes clinical investigation of the cause, differentiation from cardiac or ENT causes and, where needed, referral for additional testing.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Θεραπευτική προσέγγιση", en: "Treatment approach" },
        body: {
          el: [
            "Ανάλογα με τη διάγνωση, ο σχεδιασμός μπορεί να περιλαμβάνει θεραπεία, χειρισμούς αποκατάστασης (π.χ. για τον ίλιγγο θέσης), τροποποίηση αγωγής ή παραπομπή σε συνεργαζόμενο ειδικό.",
            "Η σαφής εξήγηση της αιτίας βοηθά τον ασθενή να διαχειριστεί το σύμπτωμα με ασφάλεια.",
          ],
          en: [
            "Depending on the diagnosis, the plan may include treatment, repositioning manoeuvres (e.g. for positional vertigo), medication adjustment or referral to a partner specialist.",
            "A clear explanation of the cause helps the patient manage the symptom safely.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Επείγον: ξαφνική, έντονη ζάλη με διπλωπία, δυσκολία ομιλίας, αδυναμία ή μούδιασμα — καλέστε το 166",
        "Επαναλαμβανόμενη ζάλη ή ίλιγγος που επηρεάζει την καθημερινότητα",
        "Ζάλη με έντονο πονοκέφαλο ή πρόσφατο τραυματισμό",
        "Αστάθεια βάδισης ή επαναλαμβανόμενες πτώσεις",
      ],
      en: [
        "Emergency: sudden, severe dizziness with double vision, speech difficulty, weakness or numbness — call emergency services",
        "Recurrent dizziness or vertigo that affects daily life",
        "Dizziness with a severe headache or a recent injury",
        "Gait unsteadiness or recurrent falls",
      ],
    },
    faq: [
      {
        q: { el: "Πότε η ζάλη χρειάζεται άμεση εκτίμηση;", en: "When does dizziness need urgent assessment?" },
        a: {
          el: "Άμεση εκτίμηση χρειάζεται αν η ζάλη είναι ξαφνική και έντονη και συνοδεύεται από διπλωπία, δυσκολία ομιλίας, αδυναμία, μούδιασμα ή έντονο πονοκέφαλο. Αυτά τα σημεία μπορεί να υποδηλώνουν σοβαρότερη αιτία.",
          en: "Urgent assessment is needed if dizziness is sudden and severe and accompanied by double vision, speech difficulty, weakness, numbness or a severe headache. These features may indicate a more serious cause.",
        },
      },
      {
        q: { el: "Ο ίλιγγος θέσης (BPPV) αντιμετωπίζεται;", en: "Can positional vertigo (BPPV) be treated?" },
        a: {
          el: "Ναι. Ο καλοήθης παροξυσμικός ίλιγγος θέσης συχνά αντιμετωπίζεται αποτελεσματικά με ειδικούς χειρισμούς επανατοποθέτησης, μετά από σωστή διάγνωση.",
          en: "Yes. Benign paroxysmal positional vertigo is often treated effectively with specific repositioning manoeuvres, after a correct diagnosis.",
        },
      },
      {
        q: { el: "Ποια είναι η διαφορά ζάλης και ιλίγγου;", en: "What's the difference between dizziness and vertigo?" },
        a: {
          el: "Ο ίλιγγος είναι η αίσθηση περιστροφής, ενώ η ζάλη είναι μια πιο γενική αίσθηση αστάθειας ή σκοτοδίνης.",
          en: "Vertigo is a spinning sensation, while dizziness is a more general feeling of unsteadiness or light-headedness.",
        },
      },
      {
        q: { el: "Μπορεί η ημικρανία να προκαλεί ίλιγγο;", en: "Can migraine cause vertigo?" },
        a: {
          el: "Ναι. Η αιθουσαία ημικρανία μπορεί να προκαλεί επεισόδια ιλίγγου, με ή χωρίς πονοκέφαλο.",
          en: "Yes. Vestibular migraine can cause episodes of vertigo, with or without headache.",
        },
      },
      {
        q: { el: "Χρειάζομαι μαγνητική για τη ζάλη;", en: "Do I need an MRI for dizziness?" },
        a: {
          el: "Όχι πάντα. Η ανάγκη για απεικόνιση καθορίζεται από την κλινική εικόνα και τα ευρήματα της εξέτασης.",
          en: "Not always. The need for imaging is determined by the clinical picture and examination findings.",
        },
      },
      {
        q: { el: "Πόσο συχνά υποτροπιάζει ο ίλιγγος;", en: "How often does vertigo recur?" },
        a: {
          el: "Εξαρτάται από την αιτία. Ορισμένες μορφές, όπως ο ίλιγγος θέσης, μπορεί να υποτροπιάζουν, αλλά συχνά αντιμετωπίζονται αποτελεσματικά.",
          en: "It depends on the cause. Some forms, such as positional vertigo, may recur, but are often treated effectively.",
        },
      },
    ],
  },
  {
    slug: { el: "aggeiaka-egkefalika", en: "stroke-tia" },
    icon: "HeartPulse",
    name: { el: "Αγγειακά Εγκεφαλικά (ΑΕΕ / TIA)", en: "Stroke & TIA" },
    metaTitle: {
      el: "Αγγειακό Εγκεφαλικό & TIA — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Stroke & TIA — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Αξιολόγηση μετά από εγκεφαλικό (ΑΕΕ) ή παροδικό ισχαιμικό επεισόδιο (TIA) και δευτερογενής πρόληψη από νευρολόγο στην Αθήνα.",
      en: "Assessment after stroke or transient ischaemic attack (TIA) and secondary prevention by a neurologist in Athens.",
    },
    excerpt: {
      el: "Νευρολογική εκτίμηση μετά από ΑΕΕ ή TIA, με έμφαση στην αναγνώριση παραγόντων κινδύνου και τη δευτερογενή πρόληψη.",
      en: "Neurological assessment after stroke or TIA, focused on identifying risk factors and secondary prevention.",
    },
    intro: {
      el: "Αξιολόγηση μετά το οξύ επεισόδιο και οργανωμένο σχέδιο δευτερογενούς πρόληψης, με πιστοποίηση NIHSS.",
      en: "Assessment after the acute event and an organised secondary-prevention plan, with NIHSS certification.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Τα αγγειακά εγκεφαλικά νοσήματα αφορούν τη διαταραχή της αιμάτωσης του εγκεφάλου — από το παροδικό ισχαιμικό επεισόδιο (TIA) έως το πλήρες αγγειακό εγκεφαλικό επεισόδιο (ΑΕΕ).",
            "Το εγκεφαλικό είναι επείγον. Η άμεση αντιμετώπιση στην οξεία φάση και, στη συνέχεια, ένα οργανωμένο πλάνο δευτερογενούς πρόληψης είναι καθοριστικά για την έκβαση.",
          ],
          en: [
            "Cerebrovascular disease concerns the disruption of blood supply to the brain — from a transient ischaemic attack (TIA) to a full stroke.",
            "Stroke is an emergency. Immediate treatment in the acute phase and, afterwards, an organised secondary-prevention plan are decisive for the outcome.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Σημεία συναγερμού (FAST)", en: "Warning signs (FAST)" },
        body: {
          el: ["Η έγκαιρη αναγνώριση των σημείων του εγκεφαλικού και η άμεση κλήση του 166 σώζουν ζωές και εγκεφαλικό ιστό. Το διεθνές ακρωνύμιο FAST είναι ένας απλός τρόπος να θυμάστε τα βασικά σημεία: Face (πρόσωπο), Arms (χέρια), Speech (ομιλία), Time (χρόνος)."],
          en: ["Recognising the signs of a stroke and calling emergency services immediately saves lives and brain tissue. The international FAST acronym is an easy way to remember the main warning signs: Face, Arms, Speech and Time."],
        },
        list: {
          el: [
            "F — Face (Πρόσωπο): ξαφνική ασυμμετρία ή μούδιασμα στο πρόσωπο",
            "A — Arms (Χέρια): ξαφνική αδυναμία ή μούδιασμα σε ένα χέρι ή πόδι",
            "S — Speech (Ομιλία): διαταραχή, ασάφεια ή απώλεια ομιλίας",
            "T — Time (Χρόνος): καλέστε αμέσως το 166, ακόμη και αν τα συμπτώματα υποχωρήσουν",
          ],
          en: [
            "F — Face: sudden facial drooping, asymmetry or numbness",
            "A — Arms: sudden weakness or numbness in one arm or leg",
            "S — Speech: speech that is slurred, confused or lost",
            "T — Time: call emergency services immediately, even if symptoms improve",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Παράγοντες κινδύνου", en: "Risk factors" },
        body: {
          el: ["Πολλοί παράγοντες κινδύνου είναι τροποποιήσιμοι, και η διαχείρισή τους μειώνει σημαντικά τον κίνδυνο:"],
          en: ["Many risk factors are modifiable, and managing them significantly reduces the risk:"],
        },
        list: {
          el: ["Αρτηριακή υπέρταση", "Σακχαρώδης διαβήτης", "Υψηλά λιπίδια (χοληστερόλη)", "Κολπική μαρμαρυγή", "Κάπνισμα και καθιστική ζωή"],
          en: ["High blood pressure", "Diabetes", "High lipids (cholesterol)", "Atrial fibrillation", "Smoking and a sedentary lifestyle"],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Αξιολόγηση μετά το επεισόδιο", en: "Assessment after the event" },
        body: {
          el: [
            "Μετά το οξύ στάδιο, η νευρολογική παρακολούθηση εστιάζει στην αξιολόγηση των παραγόντων κινδύνου (πίεση, σάκχαρο, λιπίδια, κολπική μαρμαρυγή), στην ερμηνεία της απεικόνισης και στη νευρολογική εξέταση για την εκτίμηση των υπολειπόμενων συμπτωμάτων.",
          ],
          en: [
            "After the acute phase, neurological follow-up focuses on assessing risk factors (blood pressure, blood sugar, lipids, atrial fibrillation), interpreting imaging and performing a neurological examination to evaluate any residual symptoms.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Δευτερογενής πρόληψη", en: "Secondary prevention" },
        body: {
          el: [
            "Ο σχεδιασμός της δευτερογενούς πρόληψης έχει στόχο να μειώσει τον κίνδυνο νέου επεισοδίου και περιλαμβάνει εξατομικευμένη αγωγή, ρύθμιση των παραγόντων κινδύνου και συντονισμό με άλλες ειδικότητες όπου χρειάζεται.",
            "Η υποστήριξη της αποκατάστασης αποτελεί μέρος της συνολικής φροντίδας. Για ασθενείς με δυσκολία μετακίνησης είναι διαθέσιμη και η κατ' οίκον νευρολογική εξέταση.",
          ],
          en: [
            "The secondary-prevention plan aims to reduce the risk of a new event and includes individualised treatment, control of risk factors and coordination with other specialties where needed.",
            "Support for rehabilitation is part of the overall care. For patients with mobility difficulties, a home neurological visit is also available.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Επείγον: οποιοδήποτε ξαφνικό σημείο FAST — καλέστε αμέσως το 166",
        "Μετά από παροδικό ισχαιμικό επεισόδιο (TIA), ακόμη κι αν τα συμπτώματα πέρασαν",
        "Για οργανωμένη δευτερογενή πρόληψη μετά από εγκεφαλικό",
        "Για αξιολόγηση και διαχείριση παραγόντων κινδύνου",
      ],
      en: [
        "Emergency: any sudden FAST sign — call emergency services immediately",
        "After a transient ischaemic attack (TIA), even if symptoms have passed",
        "For organised secondary prevention after a stroke",
        "For assessment and management of risk factors",
      ],
    },
    faq: [
      {
        q: { el: "Το TIA είναι επικίνδυνο αν τα συμπτώματα πέρασαν;", en: "Is a TIA dangerous if the symptoms passed?" },
        a: {
          el: "Ναι. Ακόμη κι αν τα συμπτώματα υποχώρησαν πλήρως, το παροδικό ισχαιμικό επεισόδιο αυξάνει τον κίνδυνο μελλοντικού εγκεφαλικού και απαιτεί γρήγορη νευρολογική διερεύνηση και έναρξη πρόληψης.",
          en: "Yes. Even if symptoms fully resolved, a TIA increases the risk of a future stroke and requires rapid neurological investigation and the start of prevention.",
        },
      },
      {
        q: { el: "Πώς μπορώ να μειώσω τον κίνδυνο νέου εγκεφαλικού;", en: "How can I reduce the risk of another stroke?" },
        a: {
          el: "Η ρύθμιση της πίεσης, του σακχάρου και των λιπιδίων, η αντιμετώπιση της κολπικής μαρμαρυγής, η διακοπή του καπνίσματος και η σταθερή λήψη της αγωγής μειώνουν σημαντικά τον κίνδυνο. Το πλάνο εξατομικεύεται στην επίσκεψη.",
          en: "Controlling blood pressure, blood sugar and lipids, treating atrial fibrillation, stopping smoking and taking medication consistently significantly reduce the risk. The plan is individualised at the visit.",
        },
      },
      {
        q: { el: "Τι σημαίνει το ακρωνύμιο FAST;", en: "What does FAST stand for?" },
        a: {
          el: "Πρόσωπο (Face), Χέρι (Arm), Ομιλία (Speech), Χρόνος (Time) — τα βασικά σημεία για γρήγορη αναγνώριση εγκεφαλικού.",
          en: "Face, Arm, Speech, Time — the key signs for quickly recognising a stroke.",
        },
      },
      {
        q: { el: "Πόσο γρήγορα πρέπει να αντιδράσω;", en: "How quickly must I act?" },
        a: {
          el: "Άμεσα. Σε κάθε υποψία εγκεφαλικού καλέστε επειγόντως το 166 — ο χρόνος είναι κρίσιμος για τον εγκέφαλο.",
          en: "Immediately. At any suspicion of a stroke call emergency services — time is critical for the brain.",
        },
      },
      {
        q: { el: "Μπορεί να επαναληφθεί ένα εγκεφαλικό;", en: "Can a stroke happen again?" },
        a: {
          el: "Ναι, γι' αυτό η δευτερογενής πρόληψη με ρύθμιση των παραγόντων κινδύνου και αγωγή είναι απαραίτητη.",
          en: "Yes, which is why secondary prevention with risk-factor control and medication is essential.",
        },
      },
      {
        q: { el: "Χρειάζεται αποκατάσταση μετά το εγκεφαλικό;", en: "Is rehabilitation needed after a stroke?" },
        a: {
          el: "Συχνά ναι. Η αποκατάσταση (φυσικοθεραπεία, λογοθεραπεία, εργοθεραπεία) υποστηρίζει την ανάρρωση· η νευρολογική παρακολούθηση συντονίζει τη συνολική φροντίδα.",
          en: "Often yes. Rehabilitation (physiotherapy, speech therapy, occupational therapy) supports recovery; neurological follow-up coordinates the overall care.",
        },
      },
    ],
  },
  {
    slug: { el: "sklirynsi-kata-plakas", en: "multiple-sclerosis" },
    icon: "Network",
    name: { el: "Σκλήρυνση κατά Πλάκας (ΣΚΠ)", en: "Multiple Sclerosis (MS)" },
    metaTitle: {
      el: "Σκλήρυνση κατά Πλάκας — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Multiple Sclerosis — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Διάγνωση και παρακολούθηση της σκλήρυνσης κατά πλάκας (ΣΚΠ) από νευρολόγο στην Αθήνα. Ερμηνεία μαγνητικής, τροποποιητικές θεραπείες και εξατομικευμένη φροντίδα.",
      en: "Diagnosis and monitoring of multiple sclerosis (MS) by a neurologist in Athens. MRI interpretation, disease-modifying therapies and individualised care.",
    },
    excerpt: {
      el: "Ολοκληρωμένη προσέγγιση της σκλήρυνσης κατά πλάκας — από τη διάγνωση με μαγνητική έως τις σύγχρονες τροποποιητικές θεραπείες και τη μακροχρόνια παρακολούθηση.",
      en: "A comprehensive approach to multiple sclerosis — from MRI-based diagnosis to modern disease-modifying therapies and long-term monitoring.",
    },
    intro: {
      el: "Διάγνωση, θεραπεία και μακροχρόνια παρακολούθηση της σκλήρυνσης κατά πλάκας, με έμφαση στην έγκαιρη αντιμετώπιση και την ποιότητα ζωής.",
      en: "Diagnosis, treatment and long-term monitoring of multiple sclerosis, with emphasis on early management and quality of life.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Η σκλήρυνση κατά πλάκας (ΣΚΠ) είναι μια χρόνια, αυτοάνοση πάθηση του κεντρικού νευρικού συστήματος, στην οποία το ανοσοποιητικό σύστημα προσβάλλει τη μυελίνη — το προστατευτικό περίβλημα των νευρικών ινών. Αυτό διαταράσσει τη μετάδοση των μηνυμάτων μεταξύ εγκεφάλου και σώματος.",
            "Εμφανίζεται συχνότερα σε νέους ενήλικες και πιο συχνά στις γυναίκες. Η πιο συνηθισμένη μορφή είναι η υποτροπιάζουσα-διαλείπουσα, με περιόδους εξάρσεων και υφέσεων. Η έγκαιρη διάγνωση και θεραπεία έχουν καθοριστική σημασία για την πορεία της νόσου.",
          ],
          en: [
            "Multiple sclerosis (MS) is a chronic, autoimmune condition of the central nervous system, in which the immune system attacks myelin — the protective sheath of nerve fibres. This disrupts the transmission of messages between the brain and the body.",
            "It appears most often in young adults and more frequently in women. The most common form is relapsing-remitting, with periods of relapse and remission. Early diagnosis and treatment are decisive for the course of the disease.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα", en: "Symptoms" },
        body: {
          el: ["Τα συμπτώματα ποικίλλουν ανάλογα με την περιοχή του νευρικού συστήματος που προσβάλλεται και μπορεί να εμφανίζονται σε ώσεις:"],
          en: ["Symptoms vary depending on the area of the nervous system affected and may appear in relapses:"],
        },
        list: {
          el: [
            "Διαταραχές όρασης (οπτική νευρίτιδα, θάμβος, διπλωπία)",
            "Μούδιασμα ή μυρμήγκιασμα σε άκρα ή πρόσωπο",
            "Μυϊκή αδυναμία και διαταραχές ισορροπίας/βάδισης",
            "Έντονη κόπωση, δυσκολία συγκέντρωσης",
            "Διαταραχές ούρησης ή εντέρου",
          ],
          en: [
            "Visual disturbances (optic neuritis, blurring, double vision)",
            "Numbness or tingling in the limbs or face",
            "Muscle weakness and balance/gait problems",
            "Marked fatigue, difficulty concentrating",
            "Bladder or bowel disturbances",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Αίτια & παράγοντες κινδύνου", en: "Causes & risk factors" },
        body: {
          el: [
            "Το ακριβές αίτιο της ΣΚΠ δεν είναι γνωστό. Θεωρείται ότι προκύπτει από συνδυασμό γενετικής προδιάθεσης και περιβαλλοντικών παραγόντων, όπως η ανεπάρκεια βιταμίνης D, το κάπνισμα και η προηγηθείσα λοίμωξη από τον ιό Epstein-Barr.",
            "Η ΣΚΠ δεν είναι άμεσα κληρονομική νόσος, αν και το οικογενειακό ιστορικό μπορεί να αυξάνει ελαφρώς τον κίνδυνο.",
          ],
          en: [
            "The exact cause of MS is unknown. It is thought to arise from a combination of genetic predisposition and environmental factors, such as vitamin D deficiency, smoking and prior Epstein-Barr virus infection.",
            "MS is not directly inherited, although a family history may slightly increase the risk.",
          ],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση", en: "Diagnosis" },
        body: {
          el: [
            "Η διάγνωση στηρίζεται στον συνδυασμό της κλινικής εικόνας με τη μαγνητική τομογραφία (MRI) εγκεφάλου και νωτιαίου μυελού, η οποία αναδεικνύει τις χαρακτηριστικές απομυελινωτικές βλάβες. Χρησιμοποιούνται τα διεθνή αναθεωρημένα κριτήρια McDonald, που απαιτούν διασπορά των βλαβών στον χώρο και στον χρόνο.",
            "Όπου χρειάζεται, ο έλεγχος συμπληρώνεται με οσφυονωτιαία παρακέντηση (ολιγοκλωνικές ζώνες) και προκλητά δυναμικά. Η εξειδικευμένη ερμηνεία της μαγνητικής είναι κρίσιμη για τη σωστή διάγνωση και παρακολούθηση.",
          ],
          en: [
            "Diagnosis rests on combining the clinical picture with MRI of the brain and spinal cord, which reveals the characteristic demyelinating lesions. The international revised McDonald criteria are used, requiring dissemination of lesions in space and time.",
            "Where needed, the work-up is completed with a lumbar puncture (oligoclonal bands) and evoked potentials. Expert MRI interpretation is critical for correct diagnosis and monitoring.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Θεραπευτική προσέγγιση", en: "Treatment approach" },
        body: {
          el: [
            "Η σύγχρονη αντιμετώπιση της ΣΚΠ βασίζεται στις τροποποιητικές της νόσου θεραπείες (DMTs), που μειώνουν τη συχνότητα και τη βαρύτητα των εξάρσεων και επιβραδύνουν την εξέλιξη. Η επιλογή εξατομικεύεται με βάση τη μορφή, τη δραστηριότητα της νόσου και το προφίλ του ασθενούς.",
            "Παράλληλα, αντιμετωπίζονται οι οξείες εξάρσεις, τα επιμέρους συμπτώματα (κόπωση, σπαστικότητα, πόνος) και γίνεται τακτική παρακολούθηση με επαναληπτικές μαγνητικές, σε συνεργασία με ομάδα ειδικών όπου χρειάζεται.",
          ],
          en: [
            "Modern MS management is based on disease-modifying therapies (DMTs), which reduce the frequency and severity of relapses and slow progression. The choice is individualised according to the form, disease activity and patient profile.",
            "In parallel, acute relapses and individual symptoms (fatigue, spasticity, pain) are managed, with regular monitoring via follow-up MRI and, where needed, a multidisciplinary team.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Επείγον: ξαφνική απώλεια όρασης, έντονη αδυναμία ή διαταραχή ομιλίας — άμεση εκτίμηση",
        "Νέα νευρολογικά συμπτώματα που διαρκούν πάνω από 24 ώρες",
        "Υποψία έξαρσης σε γνωστή ΣΚΠ",
        "Για τακτική παρακολούθηση και επανεκτίμηση της αγωγής",
      ],
      en: [
        "Emergency: sudden loss of vision, marked weakness or speech disturbance — seek immediate assessment",
        "New neurological symptoms lasting more than 24 hours",
        "A suspected relapse in known MS",
        "For regular follow-up and treatment review",
      ],
    },
    faq: [
      {
        q: { el: "Θεραπεύεται η σκλήρυνση κατά πλάκας;", en: "Can multiple sclerosis be cured?" },
        a: {
          el: "Δεν υπάρχει επί του παρόντος οριστική θεραπεία, όμως οι σύγχρονες τροποποιητικές θεραπείες ελέγχουν αποτελεσματικά τη νόσο, μειώνουν τις εξάρσεις και επιβραδύνουν την εξέλιξη, επιτρέποντας σε πολλούς ασθενείς μια πλήρη και ενεργή ζωή.",
          en: "There is currently no definitive cure, but modern disease-modifying therapies control the disease effectively, reduce relapses and slow progression, allowing many patients a full and active life.",
        },
      },
      {
        q: { el: "Είναι κληρονομική η ΣΚΠ;", en: "Is MS hereditary?" },
        a: {
          el: "Η ΣΚΠ δεν κληρονομείται άμεσα. Υπάρχει μικρή αύξηση του κινδύνου όταν υπάρχει οικογενειακό ιστορικό, αλλά η πλειονότητα των ασθενών δεν έχει συγγενή με τη νόσο.",
          en: "MS is not directly inherited. There is a small increase in risk with a family history, but the majority of patients have no relative with the disease.",
        },
      },
      {
        q: { el: "Μπορώ να συνεχίσω να εργάζομαι με ΣΚΠ;", en: "Can I keep working with MS?" },
        a: {
          el: "Οι περισσότεροι ασθενείς με ΣΚΠ, ιδίως με έγκαιρη διάγνωση και σωστή αγωγή, συνεχίζουν να εργάζονται και να διατηρούν ενεργή καθημερινότητα. Το πλάνο προσαρμόζεται στις ανάγκες κάθε ανθρώπου.",
          en: "Most patients with MS, especially with early diagnosis and appropriate treatment, continue to work and maintain an active daily life. The plan is adapted to each person's needs.",
        },
      },
      {
        q: { el: "Η ΣΚΠ οδηγεί πάντα σε αναπηρία;", en: "Does MS always lead to disability?" },
        a: {
          el: "Όχι. Με έγκαιρη διάγνωση και σύγχρονες θεραπείες, πολλοί ασθενείς διατηρούν καλή λειτουργικότητα για δεκαετίες.",
          en: "No. With early diagnosis and modern therapies, many patients maintain good function for decades.",
        },
      },
      {
        q: { el: "Επηρεάζει η ΣΚΠ την εγκυμοσύνη;", en: "Does MS affect pregnancy?" },
        a: {
          el: "Οι περισσότερες γυναίκες με ΣΚΠ μπορούν να κυοφορήσουν με ασφάλεια. Η αγωγή σχεδιάζεται από πριν σε συνεργασία με τον νευρολόγο.",
          en: "Most women with MS can have a safe pregnancy. Treatment is planned in advance together with the neurologist.",
        },
      },
      {
        q: { el: "Τι μπορεί να πυροδοτήσει μια έξαρση;", en: "What can trigger a relapse?" },
        a: {
          el: "Λοιμώξεις, έντονο στρες και ο πυρετός μπορεί να συμβάλλουν. Η σταθερή αγωγή βοηθά στη μείωση των εξάρσεων.",
          en: "Infections, intense stress and fever may contribute. Consistent treatment helps reduce relapses.",
        },
      },
    ],
  },
  {
    slug: { el: "diataraches-ypnou", en: "sleep-disorders" },
    icon: "Moon",
    name: { el: "Διαταραχές Ύπνου", en: "Sleep Disorders" },
    metaTitle: {
      el: "Διαταραχές Ύπνου — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Sleep Disorders — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Διερεύνηση και αντιμετώπιση αϋπνίας, συνδρόμου ανήσυχων ποδιών και άλλων διαταραχών ύπνου από νευρολόγο στην Αθήνα, με ολιστική προσέγγιση.",
      en: "Investigation and management of insomnia, restless legs syndrome and other sleep disorders by a neurologist in Athens, with a holistic approach.",
    },
    excerpt: {
      el: "Στοχευμένη αξιολόγηση της αϋπνίας, της υπερβολικής υπνηλίας και του συνδρόμου ανήσυχων ποδιών, με αναγνώριση και αντιμετώπιση της υποκείμενης αιτίας.",
      en: "Targeted assessment of insomnia, excessive sleepiness and restless legs syndrome, identifying and treating the underlying cause.",
    },
    intro: {
      el: "Ολιστική αξιολόγηση των διαταραχών ύπνου, με στόχο την αποκατάσταση ενός ξεκούραστου ύπνου και τη βελτίωση της ποιότητας ζωής.",
      en: "A holistic assessment of sleep disorders, aiming to restore restful sleep and improve quality of life.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Ο ύπνος είναι θεμελιώδης για την υγεία του εγκεφάλου. Οι διαταραχές ύπνου περιλαμβάνουν την αϋπνία, το σύνδρομο ανήσυχων ποδιών, την υπερβολική ημερήσια υπνηλία και τις διαταραχές συμπεριφοράς κατά τον ύπνο (π.χ. στη φάση REM).",
            "Συχνά συνδέονται με νευρολογικές παθήσεις — όπως η νόσος Πάρκινσον ή οι κεφαλαλγίες — γι' αυτό και η νευρολογική αξιολόγηση έχει ιδιαίτερη αξία.",
          ],
          en: [
            "Sleep is fundamental to brain health. Sleep disorders include insomnia, restless legs syndrome, excessive daytime sleepiness and sleep-behaviour disorders (e.g. in REM sleep).",
            "They are often linked to neurological conditions — such as Parkinson's disease or headaches — which is why a neurological assessment is particularly valuable.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα", en: "Symptoms" },
        body: {
          el: ["Τα συμπτώματα ενδέχεται να επηρεάζουν τόσο τον νυχτερινό ύπνο όσο και τη λειτουργικότητα κατά τη διάρκεια της ημέρας:"],
          en: ["Symptoms may affect both night-time sleep and daytime functioning:"],
        },
        list: {
          el: [
            "Δυσκολία στην έναρξη ή τη διατήρηση του ύπνου",
            "Υπερβολική υπνηλία ή κόπωση την ημέρα",
            "Μη αναζωογονητικός ύπνος",
            "Δυσάρεστη αίσθηση/ανάγκη κίνησης στα πόδια το βράδυ",
            "Εκδραμάτιση ονείρων στον ύπνο",
          ],
          en: [
            "Difficulty falling or staying asleep",
            "Excessive daytime sleepiness or fatigue",
            "Unrefreshing sleep",
            "An unpleasant sensation / urge to move the legs at night",
            "Marked movement or 'acting out' dreams during sleep",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Αίτια", en: "Causes" },
        body: {
          el: [
            "Οι διαταραχές ύπνου μπορεί να οφείλονται σε στρες και άγχος, κακή υγιεινή ύπνου, νευρολογικές παθήσεις (π.χ. σύνδρομο ανήσυχων ποδιών, Πάρκινσον), παθήσεις άλλων συστημάτων, ορισμένα φάρμακα ή την υπερβολική κατανάλωση καφεΐνης και οθονών πριν τον ύπνο.",
          ],
          en: [
            "Sleep disorders may be due to stress and anxiety, poor sleep hygiene, neurological conditions (e.g. restless legs syndrome, Parkinson's), disorders of other systems, certain medications or excessive caffeine and screen use before bed.",
          ],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση", en: "Diagnosis" },
        body: {
          el: [
            "Η αξιολόγηση ξεκινά από λεπτομερές ιστορικό ύπνου και, όπου βοηθά, από ημερολόγιο ύπνου. Η νευρολογική εξέταση αποσκοπεί στην αναγνώριση υποκείμενων αιτίων.",
            "Όπου χρειάζεται, γίνεται παραπομπή για μελέτη ύπνου (πολυυπνογραφία) σε συνεργαζόμενο κέντρο, ιδίως όταν υπάρχει υποψία υπνικής άπνοιας.",
          ],
          en: [
            "Assessment begins with a detailed sleep history and, where helpful, a sleep diary. The neurological examination aims to identify underlying causes.",
            "Where needed, referral is made for a sleep study (polysomnography) at a partner centre, especially when a sleep-related breathing disorder is suspected.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Θεραπευτική προσέγγιση", en: "Treatment approach" },
        body: {
          el: [
            "Η προσέγγιση είναι εξατομικευμένη και ολιστική. Θεμέλιο αποτελεί η βελτίωση της υγιεινής του ύπνου και οι μη φαρμακευτικές τεχνικές, ενώ αντιμετωπίζεται πάντοτε το υποκείμενο αίτιο (π.χ. σύνδρομο ανήσυχων ποδιών, άγχος).",
            "Φαρμακευτική αγωγή χρησιμοποιείται με φειδώ και μόνο όταν ενδείκνυται, αποφεύγοντας τη μακροχρόνια εξάρτηση από υπναγωγά.",
          ],
          en: [
            "The approach is individualised and holistic. Improving sleep hygiene and non-pharmacological techniques are the foundation, while the underlying cause is always addressed (e.g. restless legs syndrome, anxiety).",
            "Medication is used sparingly and only when indicated, avoiding long-term dependence on sleeping pills.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Χρόνια αϋπνία που επηρεάζει τη λειτουργικότητα και τη διάθεση",
        "Υπερβολική υπνηλία ή επεισόδια ακούσιου ύπνου κατά τη διάρκεια της ημέρας",
        "Εκδραμάτιση ονείρων στον ύπνο",
        "Ενοχλητικό σύνδρομο ανήσυχων ποδιών που διαταράσσει τον ύπνο",
      ],
      en: [
        "Chronic insomnia that affects functioning and mood",
        "Excessive sleepiness or involuntary sleep episodes during the day",
        "Marked movement or 'acting out' dreams during sleep",
        "Troublesome restless legs syndrome that disrupts sleep",
      ],
    },
    faq: [
      {
        q: { el: "Πότε η αϋπνία θεωρείται πρόβλημα;", en: "When is insomnia considered a problem?" },
        a: {
          el: "Όταν η δυσκολία στον ύπνο εμφανίζεται τακτικά (συνήθως 3 ή περισσότερες νύχτες την εβδομάδα) για αρκετές εβδομάδες και επηρεάζει τη διάθεση, τη συγκέντρωση ή τη λειτουργικότητα την ημέρα, αξίζει να διερευνηθεί.",
          en: "When difficulty sleeping occurs regularly (usually 3 or more nights a week) for several weeks and affects mood, concentration or daytime functioning, it is worth investigating.",
        },
      },
      {
        q: { el: "Χρειάζεται πάντα μελέτη ύπνου;", en: "Is a sleep study always needed?" },
        a: {
          el: "Όχι. Πολλές διαταραχές ύπνου διαγιγνώσκονται κλινικά. Η μελέτη ύπνου (πολυυπνογραφία) συνιστάται σε επιλεγμένες περιπτώσεις, ιδίως όταν υπάρχει υποψία υπνικής άπνοιας.",
          en: "No. Many sleep disorders are diagnosed clinically. A sleep study (polysomnography) is recommended in selected cases, especially when a sleep-related breathing disorder is suspected.",
        },
      },
      {
        q: { el: "Είναι η λύση τα υπναγωγά χάπια;", en: "Are sleeping pills the answer?" },
        a: {
          el: "Τα υπναγωγά μπορεί να βοηθήσουν βραχυπρόθεσμα, αλλά δεν αποτελούν λύση από μόνα τους. Στόχος είναι η αντιμετώπιση της υποκείμενης αιτίας και η βελτίωση της υγιεινής του ύπνου, ώστε να αποφευχθεί η μακροχρόνια εξάρτηση.",
          en: "Sleeping pills can help in the short term but are not a solution on their own. The aim is to treat the underlying cause and improve sleep hygiene, to avoid long-term dependence.",
        },
      },
      {
        q: { el: "Πόσες ώρες ύπνου χρειάζομαι;", en: "How many hours of sleep do I need?" },
        a: {
          el: "Οι περισσότεροι ενήλικες χρειάζονται 7–9 ώρες. Σημασία έχει και η ποιότητα, όχι μόνο η διάρκεια.",
          en: "Most adults need 7–9 hours. Quality matters too, not just duration.",
        },
      },
      {
        q: { el: "Τι είναι το σύνδρομο ανήσυχων ποδιών;", en: "What is restless legs syndrome?" },
        a: {
          el: "Μια δυσάρεστη ανάγκη κίνησης των ποδιών, συνήθως το βράδυ, που διαταράσσει τον ύπνο και συχνά αντιμετωπίζεται αποτελεσματικά.",
          en: "An unpleasant urge to move the legs, usually in the evening, that disrupts sleep and is often treated effectively.",
        },
      },
      {
        q: { el: "Βοηθά η υγιεινή του ύπνου;", en: "Does sleep hygiene help?" },
        a: {
          el: "Πολύ. Σταθερό ωράριο, περιορισμός οθονών και καφεΐνης και ήρεμο περιβάλλον βελτιώνουν σημαντικά τον ύπνο.",
          en: "A lot. A regular schedule, limiting screens and caffeine and a calm environment significantly improve sleep.",
        },
      },
    ],
  },
  {
    slug: { el: "agchos-leitourgika-neurologika", en: "anxiety-functional-neurological-disorders" },
    icon: "Waves",
    name: { el: "Άγχος & Λειτουργικά Νευρολογικά Συμπτώματα", en: "Anxiety & Functional Neurological Disorders" },
    metaTitle: {
      el: "Άγχος & Λειτουργικά Νευρολογικά Συμπτώματα — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Anxiety & Functional Neurological Disorders — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Διάγνωση και αντιμετώπιση λειτουργικών νευρολογικών συμπτωμάτων και σωματικών εκδηλώσεων του άγχους από νευρολόγο στην Αθήνα, με σαφή εξήγηση και εξατομικευμένο πλάνο.",
      en: "Diagnosis and management of functional neurological symptoms and the physical manifestations of anxiety by a neurologist in Athens, with a clear explanation and a personalised plan.",
    },
    excerpt: {
      el: "Ουδέτερη, τεκμηριωμένη προσέγγιση των λειτουργικών νευρολογικών συμπτωμάτων και των σωματικών εκδηλώσεων του άγχους, χωρίς στιγματισμό.",
      en: "A neutral, evidence-based approach to functional neurological symptoms and the physical manifestations of anxiety, without stigma.",
    },
    intro: {
      el: "Πολλά πραγματικά και ενοχλητικά συμπτώματα — ζάλη, μούδιασμα, τρόμος, αδυναμία ή «θόλωμα» — μπορεί να οφείλονται σε λειτουργική διαταραχή του νευρικού συστήματος ή σε έντονο άγχος. Η προσέγγιση είναι διαγνωστική και υποστηρικτική.",
      en: "Many real and distressing symptoms — dizziness, numbness, tremor, weakness or brain fog — can stem from a functional disorder of the nervous system or from significant anxiety. The approach is diagnostic and supportive.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Τα λειτουργικά νευρολογικά συμπτώματα (FND) είναι πραγματικά σωματικά συμπτώματα που προκύπτουν από διαταραχή στον τρόπο με τον οποίο λειτουργεί το νευρικό σύστημα, χωρίς δομική βλάβη στον εγκέφαλο ή στα νεύρα.",
            "Το άγχος, με τη σειρά του, μπορεί να εκδηλωθεί με έντονα σωματικά συμπτώματα που μιμούνται νευρολογικές παθήσεις. Και στις δύο περιπτώσεις τα συμπτώματα δεν είναι «φανταστικά» — απλώς έχουν διαφορετικό μηχανισμό.",
          ],
          en: [
            "Functional neurological symptoms (FND) are genuine physical symptoms that arise from a problem in how the nervous system functions, without structural damage to the brain or nerves.",
            "Anxiety, in turn, can present with intense physical symptoms that mimic neurological disease. In both cases the symptoms are not \u2018imaginary\u2019 — they simply have a different mechanism.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα", en: "Symptoms" },
        body: {
          el: [
            "Τα συμπτώματα ποικίλλουν σε ένταση και μπορεί να μεταβάλλονται μέσα στην ημέρα. Συχνά επιδεινώνονται με την κόπωση ή το στρες και βελτιώνονται όταν αποσπάται η προσοχή.",
          ],
          en: [
            "Symptoms vary in intensity and may fluctuate through the day. They often worsen with fatigue or stress and improve when attention is distracted.",
          ],
        },
        list: {
          el: [
            "Ζάλη, αστάθεια ή αίσθημα λιποθυμίας",
            "Μούδιασμα, μυρμήγκιασμα ή αδυναμία σε άκρο",
            "Τρόμος ή ακούσιες κινήσεις",
            "Λειτουργικές (μη επιληπτικές) κρίσεις",
            "Αίσθημα «θολώματος», δυσκολία συγκέντρωσης ή μνήμης",
          ],
          en: [
            "Dizziness, unsteadiness or a feeling of faintness",
            "Numbness, tingling or weakness in a limb",
            "Tremor or involuntary movements",
            "Functional (non-epileptic) seizures",
            "Brain fog, difficulty with concentration or memory",
          ],
        },
      },
      {
        id: "causes",
        heading: { el: "Αίτια & εκλυτικοί παράγοντες", en: "Causes & triggers" },
        body: {
          el: [
            "Συχνά συνυπάρχει έντονο ψυχολογικό φορτίο και τα συμπτώματα πυροδοτούνται από σωματικούς εκλυτικούς παράγοντες (π.χ. μια λοίμωξη ή έναν τραυματισμό). Η κατανόηση του μηχανισμού βοηθά στην αποδοχή και στη θεραπεία.",
          ],
          en: [
            "A significant psychological burden often coexists, and symptoms may be triggered by physical precipitating factors (e.g. an infection or injury). Understanding the mechanism helps with acceptance and treatment.",
          ],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση", en: "Diagnosis" },
        body: {
          el: [
            "Η διάγνωση δεν είναι απαραίτητα διάγνωση αποκλεισμού. Βασίζεται και σε θετικά κλινικά ευρήματα κατά τη νευρολογική εξέταση, που υποδεικνύουν λειτουργικό μηχανισμό.",
            "Όπου χρειάζεται, ζητούνται στοχευμένες εξετάσεις για να αποκλειστούν άλλες παθήσεις, χωρίς όμως άσκοπη υπερδιερεύνηση που αυξάνει το άγχος.",
          ],
          en: [
            "The diagnosis is not necessarily one of exclusion. It is also based on positive clinical signs during the neurological examination that point to a functional mechanism.",
            "Where needed, targeted tests are requested to rule out other conditions, without unnecessary over-investigation that increases anxiety.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Θεραπευτική προσέγγιση", en: "Treatment approach" },
        body: {
          el: [
            "Η θεραπεία ξεκινά με μια σαφή, κατανοητή εξήγηση της διάγνωσης — ένα από τα πιο σημαντικά θεραπευτικά βήματα.",
            "Το πλάνο εξατομικεύεται και μπορεί να περιλαμβάνει φυσικοθεραπεία ειδική για FND, ψυχολογική υποστήριξη ή γνωσιακή-συμπεριφορική θεραπεία, τεχνικές διαχείρισης άγχους και, όπου ενδείκνυται, φαρμακευτική αγωγή.",
          ],
          en: [
            "Treatment begins with a clear, understandable explanation of the diagnosis — one of the most important therapeutic steps.",
            "The plan is individualised and may include FND-specific physiotherapy, psychological support or cognitive-behavioural therapy, anxiety-management techniques and, where indicated, medication.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Ξαφνική, επίμονη αδυναμία, δυσαρθρία ή διαταραχή όρασης — αποκλείστε πρώτα οξέα αίτια (καλέστε το 166)",
        "Συμπτώματα που επηρεάζουν σημαντικά την καθημερινότητα ή την εργασία σας",
        "Επαναλαμβανόμενα επεισόδια ζάλης, τρόμου ή «κενού» χωρίς σαφή εξήγηση",
        "Έντονο άγχος με σωματικά συμπτώματα που σας ανησυχούν",
      ],
      en: [
        "Sudden, persistent weakness, slurred speech or visual disturbance — rule out acute causes first (call emergency services)",
        "Symptoms that significantly affect your daily life or work",
        "Recurrent episodes of dizziness, tremor or \u2018blanking out\u2019 without a clear explanation",
        "Marked anxiety with physical symptoms that worry you",
      ],
    },
    faq: [
      {
        q: { el: "Σημαίνει ότι τα συμπτώματά μου είναι «στο μυαλό μου»;", en: "Does this mean my symptoms are \u2018all in my head\u2019?" },
        a: {
          el: "Όχι. Τα λειτουργικά συμπτώματα είναι απολύτως πραγματικά. Η διαφορά είναι στον μηχανισμό: το πρόβλημα αφορά τη λειτουργία και όχι τη δομή του νευρικού συστήματος.",
          en: "No. Functional symptoms are entirely real. The difference is in the mechanism: the problem lies in the function, not the structure, of the nervous system.",
        },
      },
      {
        q: { el: "Μπορεί να βελτιωθεί ένα λειτουργικό σύμπτωμα;", en: "Can a functional symptom improve?" },
        a: {
          el: "Ναι. Με σωστή διάγνωση, εξήγηση και εξατομικευμένη θεραπεία, πολλοί ασθενείς παρουσιάζουν σημαντική ή πλήρη βελτίωση.",
          en: "Yes. With the right diagnosis, explanation and individualised treatment, many patients show significant or complete improvement.",
        },
      },
      {
        q: { el: "Χρειάζομαι μαγνητική τομογραφία;", en: "Do I need an MRI?" },
        a: {
          el: "Όχι πάντα. Οι εξετάσεις ζητούνται στοχευμένα όταν υπάρχει κλινική ένδειξη, ώστε να αποφεύγεται η άσκοπη ανησυχία.",
          en: "Not always. Tests are requested selectively when there is a clinical indication, to avoid unnecessary worry.",
        },
      },
      {
        q: { el: "Είναι το ίδιο με την κατάθλιψη ή το άγχος;", en: "Is this the same as depression or anxiety?" },
        a: {
          el: "Όχι, αν και μπορεί να συνυπάρχουν. Το άγχος μπορεί να επιδεινώνει τα συμπτώματα, αλλά τα λειτουργικά συμπτώματα εμφανίζονται και χωρίς ψυχιατρική διάγνωση.",
          en: "No, although they can coexist. Anxiety can worsen symptoms, but functional symptoms also occur without a psychiatric diagnosis.",
        },
      },
      {
        q: { el: "Ποιος συντονίζει τη θεραπεία μου;", en: "Who coordinates my treatment?" },
        a: {
          el: "Ο νευρολόγος θέτει τη διάγνωση και συντονίζει την ομάδα — φυσικοθεραπευτή, ψυχολόγο και άλλες ειδικότητες όπου χρειάζεται.",
          en: "The neurologist establishes the diagnosis and coordinates the team — physiotherapist, psychologist and other specialties where needed.",
        },
      },
      {
        q: { el: "Θα χρειαστώ φάρμακα;", en: "Will I need medication?" },
        a: {
          el: "Όχι απαραίτητα. Η φαρμακευτική αγωγή είναι ένα μόνο εργαλείο και χρησιμοποιείται επιλεκτικά, ανάλογα με τα συμπτώματα και τις συνοδές καταστάσεις.",
          en: "Not necessarily. Medication is only one tool and is used selectively, depending on symptoms and any coexisting conditions.",
        },
      },
    ],
  },
  {
    slug: { el: "neuromyika-nosimata", en: "neuromuscular-diseases" },
    icon: "Dumbbell",
    name: { el: "Νευρομυϊκά Νοσήματα", en: "Neuromuscular Diseases" },
    metaTitle: {
      el: "Νευρομυϊκά Νοσήματα — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Neuromuscular Diseases — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Διάγνωση και αντιμετώπιση νευρομυϊκών νοσημάτων — περιφερικές νευροπάθειες, μυοπάθειες, μυασθένεια και νευροπαθητικός πόνος — από νευρολόγο στην Αθήνα, με παραπομπή για ηλεκτρομυογράφημα όπου ενδείκνυται.",
      en: "Diagnosis and management of neuromuscular diseases — peripheral neuropathies, myopathies, myasthenia and neuropathic pain — by a neurologist in Athens, with referral for EMG where indicated.",
    },
    excerpt: {
      el: "Διερεύνηση μυϊκής αδυναμίας, μουδιάσματος και νευροπαθητικού πόνου, με ηλεκτροφυσιολογικό έλεγχο και στοχευμένη θεραπεία.",
      en: "Work-up of muscle weakness, numbness and neuropathic pain, with electrophysiological testing and targeted treatment.",
    },
    intro: {
      el: "Τα νευρομυϊκά νοσήματα επηρεάζουν τα περιφερικά νεύρα, τη νευρομυϊκή σύναψη ή τους μύες. Η διάγνωση στηρίζεται στη νευρολογική εξέταση και, όπου ενδείκνυται, σε παραπομπή για ηλεκτροφυσιολογικό έλεγχο (ΗΜΓ / μελέτη αγωγιμότητας).",
      en: "Neuromuscular diseases affect the peripheral nerves, the neuromuscular junction or the muscles. Diagnosis rests on the neurological examination and, where indicated, referral for electrophysiological testing (EMG / nerve conduction studies).",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Ο όρος «νευρομυϊκά νοσήματα» καλύπτει ένα ευρύ φάσμα παθήσεων που επηρεάζουν το περιφερικό νευρικό σύστημα και τους μύες — από τις πολυνευροπάθειες και τα σύνδρομα παγίδευσης νεύρου έως τις μυοπάθειες και τη μυασθένεια.",
            "Κοινό χαρακτηριστικό είναι η διαταραχή στη μετάδοση του σήματος από το νεύρο στους μύες, που εκδηλώνεται με αδυναμία, μούδιασμα ή πόνο.",
          ],
          en: [
            "The term \u2018neuromuscular diseases\u2019 covers a broad range of conditions affecting the peripheral nervous system and the muscles — from polyneuropathies and nerve entrapment syndromes to myopathies and myasthenia.",
            "Their common feature is a disturbance in signal transmission from nerve to muscle, presenting with weakness, numbness or pain.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα", en: "Symptoms" },
        body: {
          el: [
            "Τα συμπτώματα εξαρτώνται από το ποιο τμήμα επηρεάζεται (νεύρα, νευρομυϊκή σύναψη ή μύες) και μπορεί να εξελίσσονται σταδιακά ή να εμφανίζονται με διακυμάνσεις μέσα στην ημέρα.",
          ],
          en: [
            "Symptoms depend on which part is affected (nerve, junction or muscle) and may progress gradually or fluctuate through the day.",
          ],
        },
        list: {
          el: [
            "Μυϊκή αδυναμία ή εύκολη κόπωση",
            "Μούδιασμα, μυρμήγκιασμα ή αίσθημα «καψίματος»",
            "Μυϊκές κράμπες, μυϊκά «πεταρίσματα» ή απώλεια μυϊκής μάζας",
            "Δυσκολία στο βάδισμα ή στον χειρισμό αντικειμένων",
            "Διπλωπία ή πτώση βλεφάρου (σε μυασθένεια)",
          ],
          en: [
            "Muscle weakness or easy fatigue",
            "Numbness, tingling or a burning sensation",
            "Muscle cramps, twitching or wasting",
            "Difficulty walking or handling objects",
            "Double vision or a drooping eyelid (in myasthenia)",
          ],
        },
      },
      {
        id: "pain",
        heading: { el: "Πόνος (νευροπαθητικός)", en: "Pain (neuropathic)" },
        body: {
          el: [
            "Ο νευροπαθητικός πόνος προκύπτει από βλάβη ή δυσλειτουργία των ίδιων των νεύρων και διαφέρει από τον συνήθη μυοσκελετικό πόνο. Περιγράφεται συχνά ως κάψιμο, ηλεκτρικό σοκ, τσιμπήματα ή αίσθημα παγώματος.",
            "Η αντιμετώπισή του απαιτεί ειδική προσέγγιση: τα κοινά αναλγητικά συχνά δεν επαρκούν, ενώ βοηθούν φάρμακα που δρουν στη νευρική διεγερσιμότητα, σε συνδυασμό με τοπικές θεραπείες και αποκατάσταση.",
          ],
          en: [
            "Neuropathic pain arises from damage or dysfunction of the nerves themselves and differs from ordinary musculoskeletal pain. It is often described as burning, electric-shock, pins-and-needles or a freezing sensation.",
            "Managing it requires a specific approach: common painkillers are often insufficient, while medications that act on nerve excitability help, combined with topical treatments and rehabilitation.",
          ],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Διάγνωση & Παραπομπή για Ηλεκτρομυογράφημα (ΗΜΓ)", en: "Diagnosis & Referral for EMG" },
        body: {
          el: [
            "Η διερεύνηση ξεκινά με λεπτομερή νευρολογική εξέταση. Κατά περίπτωση γίνεται παραπομπή για ηλεκτρομυογράφημα (ΗΜΓ) και μελέτη ταχύτητας αγωγής των νεύρων, ώστε να εντοπιστεί το επίπεδο και η φύση της βλάβης. Οι εξετάσεις αυτές διενεργούνται σε συνεργαζόμενο διαγνωστικό κέντρο.",
            "Συμπληρωματικά μπορεί να χρειαστούν αιματολογικός έλεγχος, ανοσολογικός έλεγχος ή, σπανιότερα, βιοψία νεύρου ή μυός.",
          ],
          en: [
            "Work-up starts with a detailed neurological examination. Where appropriate, a referral is made for electromyography (EMG) and nerve conduction studies to identify the level and nature of the lesion. These tests are carried out at a partner diagnostic centre.",
            "Blood tests, immunological screening or, less often, a nerve or muscle biopsy may be needed in addition.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Θεραπευτική προσέγγιση", en: "Treatment approach" },
        body: {
          el: [
            "Η θεραπεία στοχεύει στο υποκείμενο αίτιο όπου είναι εφικτό (π.χ. ρύθμιση διαβήτη, ανοσοθεραπεία στη μυασθένεια) και στην ανακούφιση των συμπτωμάτων.",
            "Η αποκατάσταση με φυσικοθεραπεία, η προστασία των παγιδευμένων νεύρων και η διαχείριση του νευροπαθητικού πόνου αποτελούν βασικά μέρη ενός εξατομικευμένου πλάνου.",
          ],
          en: [
            "Treatment targets the underlying cause where possible (e.g. diabetes control, immunotherapy in myasthenia) and relieves symptoms.",
            "Rehabilitation with physiotherapy, protecting entrapped nerves and managing neuropathic pain are core parts of an individualised plan.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Ταχέως εξελισσόμενη αδυναμία ή δυσκολία στην αναπνοή ή στην κατάποση — ζητήστε άμεση βοήθεια",
        "Επίμονο μούδιασμα ή αδυναμία που επιδεινώνεται",
        "Νευροπαθητικός πόνος που δεν ανταποκρίνεται στα κοινά αναλγητικά",
        "Επαναλαμβανόμενες πτώσεις ή αστάθεια στο βάδισμα",
      ],
      en: [
        "Rapidly progressing weakness or difficulty breathing or swallowing — seek immediate help",
        "Persistent numbness or weakness that is worsening",
        "Neuropathic pain that does not respond to common painkillers",
        "Recurrent falls or unsteadiness when walking",
      ],
    },
    faq: [
      {
        q: { el: "Ο νευροπαθητικός πόνος θεραπεύεται;", en: "Can neuropathic pain be treated?" },
        a: {
          el: "Ναι, στις περισσότερες περιπτώσεις ελέγχεται αποτελεσματικά με στοχευμένη αγωγή, αν και συχνά χρειάζεται χρόνος και προσαρμογή της δόσης.",
          en: "Yes, in most cases it is controlled effectively with targeted treatment, though it often takes time and dose adjustment.",
        },
      },
      {
        q: { el: "Το μούδιασμα στα χέρια σημαίνει σύνδρομο καρπιαίου σωλήνα;", en: "Does hand numbness mean carpal tunnel syndrome?" },
        a: {
          el: "Συχνά ναι, αλλά όχι πάντα. Το ΗΜΓ βοηθά να επιβεβαιωθεί η παγίδευση του νεύρου και να αποκλειστούν άλλα αίτια.",
          en: "Often yes, but not always. The EMG helps confirm nerve entrapment and rule out other causes.",
        },
      },
      {
        q: { el: "Είναι κληρονομικά τα νευρομυϊκά νοσήματα;", en: "Are neuromuscular diseases hereditary?" },
        a: {
          el: "Ορισμένα είναι, αλλά πολλά είναι επίκτητα (π.χ. διαβητική νευροπάθεια ή αυτοάνοση μυασθένεια). Το ιστορικό βοηθά να διακριθούν.",
          en: "Some are, but many are acquired (e.g. diabetic neuropathy or autoimmune myasthenia). The history helps to distinguish them.",
        },
      },
      {
        q: { el: "Θα χρειαστώ φυσικοθεραπεία;", en: "Will I need physiotherapy?" },
        a: {
          el: "Σε πολλές περιπτώσεις η φυσικοθεραπεία είναι κεντρικό μέρος της θεραπείας, καθώς διατηρεί τη μυϊκή ισχύ και βελτιώνει τη λειτουργικότητα.",
          en: "In many cases physiotherapy is a central part of treatment, as it maintains muscle strength and improves function.",
        },
      },
    ],
  },
  {
    slug: { el: "alles-katastaseis", en: "other-conditions" },
    icon: "Sparkles",
    name: { el: "Άλλες Καταστάσεις", en: "Other Conditions" },
    metaTitle: {
      el: "Άλλες Νευρολογικές Καταστάσεις — Νευρολόγος Αθήνα | Δρ. Αποστολακοπούλου",
      en: "Other Neurological Conditions — Neurologist Athens | Dr. Apostolakopoulou",
    },
    metaDescription: {
      el: "Νευρολογική εκτίμηση για συμπτώματα που δεν εντάσσονται σε μία κατηγορία — τρόμος, μούδιασμα, τικ, εμβοές, μετασεισμικό σύνδρομο, νευρολογικά συμπτώματα Long-COVID και χρόνια κόπωση — από νευρολόγο στην Αθήνα.",
      en: "Neurological assessment for symptoms that don't fit a single category — tremor, numbness, tics, tinnitus, post-concussion syndrome, Long-COVID neurological symptoms and chronic fatigue — by a neurologist in Athens.",
    },
    excerpt: {
      el: "Νευρολογική αξιολόγηση για ένα ευρύ φάσμα συμπτωμάτων που χρειάζονται διερεύνηση, με σαφή κατεύθυνση και επόμενα βήματα.",
      en: "Neurological assessment for a broad range of symptoms that need investigation, with a clear direction and next steps.",
    },
    intro: {
      el: "Ορισμένα νευρολογικά συμπτώματα δεν εντάσσονται εξαρχής σε μία συγκεκριμένη πάθηση. Σε αυτές τις περιπτώσεις, μια προσεκτική νευρολογική εκτίμηση δίνει κατεύθυνση: τι χρειάζεται διερεύνηση, τι μπορεί να παρακολουθηθεί και πότε απαιτείται παραπομπή.",
      en: "Some neurological symptoms don't fit a single condition at first. In these cases, a careful neurological assessment gives direction: what needs investigation, what can be monitored and when a referral is required.",
    },
    sections: [
      {
        id: "overview",
        heading: { el: "Επισκόπηση", en: "Overview" },
        body: {
          el: [
            "Η νευρολογία καλύπτει πολύ περισσότερα από τις γνωστές παθήσεις. Πολλοί ασθενείς έρχονται με συμπτώματα που τους ανησυχούν, χωρίς να γνωρίζουν σε ποια ειδικότητα ανήκουν.",
            "Στόχος της πρώτης εκτίμησης είναι να καθησυχαστούν οι ακίνδυνες καταστάσεις και να αναγνωριστούν έγκαιρα εκείνες που χρειάζονται περαιτέρω διερεύνηση ή θεραπεία.",
          ],
          en: [
            "Neurology covers far more than the well-known conditions. Many patients arrive with symptoms that worry them without knowing which specialty they belong to.",
            "The aim of the first assessment is to reassure about harmless conditions and to identify early those that need further investigation or treatment.",
          ],
        },
      },
      {
        id: "symptoms",
        heading: { el: "Συμπτώματα που αξιολογούμε", en: "Symptoms we assess" },
        body: {
          el: [
            "Ενδεικτικά, μερικά από τα συμπτώματα και τις καταστάσεις που αξιολογούνται σε αυτό το πλαίσιο:",
          ],
          en: [
            "As examples, some of the symptoms and conditions assessed in this context:",
          ],
        },
        list: {
          el: [
            "Τρόμος ή ακούσιες κινήσεις",
            "Επίμονο μούδιασμα ή διαταραχές της αισθητικότητας",
            "Τικ και άλλες υπερκινητικές διαταραχές",
            "Εμβοές ή διαταραχές ισορροπίας",
            "Ανεξήγητη κόπωση με νευρολογικά συμπτώματα",
            "Νευραλγίες προσώπου",
            "Μετασεισμικό σύνδρομο μετά από διάσειση / κρανιοεγκεφαλική κάκωση",
            "Νευρολογικά συμπτώματα Long-COVID (π.χ. «brain fog», κεφαλαλγία, ζάλη)",
            "Χρόνια κόπωση με νευρολογική διάσταση",
          ],
          en: [
            "Tremor or involuntary movements",
            "Persistent numbness or sensory disturbances",
            "Tics and other hyperkinetic disorders",
            "Tinnitus or balance disturbances",
            "Unexplained fatigue with neurological symptoms",
            "Facial neuralgias",
            "Post-concussion syndrome after a concussion / head injury",
            "Neurological symptoms of Long-COVID (e.g. brain fog, headache, dizziness)",
            "Chronic fatigue with a neurological dimension",
          ],
        },
      },
      {
        id: "diagnosis",
        heading: { el: "Πώς γίνεται η εκτίμηση", en: "How the assessment works" },
        body: {
          el: [
            "Η αξιολόγηση ξεκινά με λεπτομερές ιστορικό και πλήρη νευρολογική εξέταση. Πολλές διαγνώσεις τίθενται κλινικά, χωρίς να χρειάζονται πολλές εξετάσεις.",
            "Όπου απαιτείται, ζητούνται στοχευμένες εξετάσεις (απεικόνιση, ηλεκτροφυσιολογικός ή αιματολογικός έλεγχος) με σαφή αιτιολόγηση για κάθε εξέταση.",
          ],
          en: [
            "The assessment starts with a detailed history and a full neurological examination. Many diagnoses are made clinically, without the need for many tests.",
            "Where required, targeted tests (imaging, electrophysiological or blood tests) are requested with a clear rationale for each one.",
          ],
        },
      },
      {
        id: "treatment",
        heading: { el: "Επόμενα βήματα & κατεύθυνση", en: "Next steps & direction" },
        body: {
          el: [
            "Μετά την εκτίμηση, λαμβάνετε μια σαφή εξήγηση: ποια είναι η πιθανή αιτία, ποιες εξετάσεις μπορεί να χρειάζονται και ποιο είναι το πλάνο παρακολούθησης ή θεραπείας.",
            "Όταν το σύμπτωμα ανήκει σε άλλη ειδικότητα, γίνεται στοχευμένη παραπομπή, ώστε να μη χαθεί χρόνος.",
          ],
          en: [
            "After the assessment, you receive a clear explanation: the likely cause, which tests (if any) are needed and the monitoring or treatment plan.",
            "When a symptom belongs to another specialty, a targeted referral is made so that no time is lost.",
          ],
        },
      },
    ],
    whenToSee: {
      el: [
        "Οξεία εμφάνιση αδυναμίας, δυσαρθρίας ή διαταραχής όρασης — καλέστε το 166",
        "Νέο ή επιδεινούμενο νευρολογικό σύμπτωμα που επιμένει",
        "Σύμπτωμα που επηρεάζει την καθημερινότητα ή σας προκαλεί ανησυχία",
        "Όταν δεν γνωρίζετε σε ποια ειδικότητα ανήκει το πρόβλημά σας",
      ],
      en: [
        "Acute onset of weakness, slurred speech or visual disturbance — call emergency services",
        "A new or worsening neurological symptom that persists",
        "A symptom that affects daily life or causes you concern",
        "When you're unsure which specialty your problem belongs to",
      ],
    },
    faq: [
      {
        q: { el: "Το σύμπτωμά μου δεν ταιριάζει πουθενά — αξίζει να δω νευρολόγο;", en: "My symptom doesn't fit anywhere — is it worth seeing a neurologist?" },
        a: {
          el: "Ναι. Μια νευρολογική εκτίμηση μπορεί να ξεκαθαρίσει αν το σύμπτωμα είναι νευρολογικής φύσης και να σας κατευθύνει σωστά, ακόμη κι αν τελικά δεν χρειάζεται θεραπεία.",
          en: "Yes. A neurological assessment can clarify whether the symptom is neurological in nature and point you in the right direction, even if no treatment is ultimately needed.",
        },
      },
      {
        q: { el: "Θα χρειαστώ οπωσδήποτε εξετάσεις;", en: "Will I definitely need tests?" },
        a: {
          el: "Όχι. Πολλές καταστάσεις διαγιγνώσκονται κλινικά. Οι εξετάσεις ζητούνται μόνο όταν αλλάζουν ουσιαστικά τη διάγνωση ή τη θεραπεία.",
          en: "No. Many conditions are diagnosed clinically. Tests are requested only when they meaningfully change the diagnosis or treatment.",
        },
      },
      {
        q: { el: "Είναι σοβαρός ο τρόμος στα χέρια μου;", en: "Is the tremor in my hands serious?" },
        a: {
          el: "Ο τρόμος έχει πολλές αιτίες, οι περισσότερες καλοήθεις. Η νευρολογική εξέταση διακρίνει, π.χ., τον ιδιοπαθή τρόμο από άλλες αιτίες.",
          en: "Tremor has many causes, most of them benign. The neurological examination distinguishes, for example, essential tremor from other causes.",
        },
      },
      {
        q: { el: "Τι είναι τα τικ;", en: "What are tics?" },
        a: {
          el: "Είναι σύντομες, επαναλαμβανόμενες ακούσιες κινήσεις ή ήχοι. Συχνά είναι καλοήθη, αλλά αξιολογούνται όταν επιμένουν ή επηρεάζουν την καθημερινότητα.",
          en: "They are brief, repetitive involuntary movements or sounds. They are often benign but are assessed when they persist or affect daily life.",
        },
      },
      {
        q: { el: "Θα με παραπέμψετε αν χρειαστεί;", en: "Will you refer me if needed?" },
        a: {
          el: "Βεβαίως. Όταν το πρόβλημα ανήκει σε άλλη ειδικότητα, γίνεται στοχευμένη παραπομπή με σαφείς οδηγίες.",
          en: "Of course. When the problem belongs to another specialty, a targeted referral is made with clear instructions.",
        },
      },
    ],
  },
];
